import { strings } from 'APP_ROOT/localization';
import CognitoUtil from '../../components/aws/cognito/cognitoUtil';
import { apiRequest, getApiBaseUrl, getAPIJWT, getLocalAPIJWT, setLocalAPIJWT, RcAPI } from '../../components/common/rcApi';
import events from 'APP_ROOT/data/events';

import BuildSettings from '../../components/buildSettings';
import { buildSettings as genericAiEventsSettings } from '../events';

export const EVENTS_THRESHOLD_TYPES = {
    CONSTANT: "CONSTANT",
    PERCENT: "PERCENT"
}

export const VIDEO_RECORDING_PROFILE_NAMES = {
    LEGACY: "legacy", // This is what Raven has used for the past years.
    STANDARD: "standard", // (HD) This is the new default, using h265
    BALANCED: "balanced", // In-between standard and extended
    EXTENDED: "extended", // This reduced the bitrate/framerate to record even longer
    MANUAL: "manual", // Not user-selectable. This would be for us to apply a custom profile manually.
}

export const INTEGRATIONS_TYPES_NAMES = { // corresponds to the "name" property of items in `integrations/types` response
    FLEETIO: "Fleetio", // Important: Capital case!
    WHIPAROUND: "WhipAround"
}

const INTEGRATIONS_ENDPOINTS = {
    FLEETIO: "https://secure.fleetio.com/api/v1/",
    WHIPAROUND: "https://api.whip-around.com/api/public/v4/"
};

const INTEGRATIONS_DETAILS = {
    FLEETIO: (data) => ({
        integrationType: INTEGRATIONS_TYPES_NAMES.FLEETIO,
        details: {
            endpoint: INTEGRATIONS_ENDPOINTS.FLEETIO,
            account_token: data.accountToken,
            token: data.apiKey
        },
        
    }),
    WHIPAROUND: (data) => ({
        integrationType: INTEGRATIONS_TYPES_NAMES.WHIPAROUND,
        details: {
            endpoint: INTEGRATIONS_ENDPOINTS.WHIPAROUND,
            api_key: data.apiKey
        }
    })
}

export const AUDIO_ALERT_TYPES = {
    NONE: "NONE",
    BEEP: "BEEP",
    VOICE: "VOICE"
}

// All event snake_names that exist in events_config
const buildEnvironment = BuildSettings.environment();
const genericAiEventsFeaturesAvailability = genericAiEventsSettings(buildEnvironment) ? genericAiEventsSettings(buildEnvironment) : {};
const generalBuildFeatures = BuildSettings.applyBuildFeatures({});
const generalEventsBuildFeatureAvailibility = generalBuildFeatures && generalBuildFeatures.global_settings && generalBuildFeatures.global_settings.events ? generalBuildFeatures.global_settings.events : {}; 
// genericAiEventsFeaturesAvailability and generalEventsBuildFeatureAvailibility are in format {key: BOOLEAN} where the key is the snake_name of the event, and the value is whether that event is available in the current build environment
// eventsConfigKeys is a list of all the unique keys from genericAiEventsFeaturesAvailability and generalEventsBuildFeatureAvailibility where the value is "true" for the current build environment
const eventsConfigKeys = Object.keys({
    ...genericAiEventsFeaturesAvailability,
    ...generalEventsBuildFeatureAvailibility
}).filter(key => genericAiEventsFeaturesAvailability[key] || generalEventsBuildFeatureAvailibility[key]);

const ALL_EVENT_NAMES = eventsConfigKeys.map((snakeName) => {
    return {
        snakeName: snakeName,
        snakeNameUpper: snakeName.toUpperCase(),
        //camelName: snakeName.replace(/(_\w)/g, function(m){return m[1].toUpperCase();}),
        pascalName: snakeName.replace(/(_\w)/g, function(m){return m[1].toUpperCase();}).replace(/^\w/, c => c.toUpperCase())
    };
});
export { ALL_EVENT_NAMES };

/*
example of ALL_EVENT_NAMES = [
    { snakeName: 'aggressive_accel', snakeNameUpper: 'AGGRESSIVE_ACCEL', pascalName: 'AggressiveAccel' },
    { snakeName: 'bad_install', snakeNameUpper: 'BAD_INSTALL', pascalName: 'BadInstall' },
    { snakeName: 'camera_obscured', snakeNameUpper: 'CAMERA_OBSCURED', pascalName: 'CameraObscured' },
    { snakeName: 'car_bumped', snakeNameUpper: 'CAR_BUMPED', pascalName: 'CarBumped' },
    { snakeName: 'cellphone_detection', snakeNameUpper: 'CELLPHONE_DETECTION', pascalName: 'CellphoneDetection' },
    { snakeName: 'distracted_detection', snakeNameUpper: 'DISTRACTED_DETECTION', pascalName: 'DistractedDetection' },
    { snakeName: 'drinking_detection', snakeNameUpper: 'DRINKING_DETECTION', pascalName: 'DrinkingDetection' },
    { snakeName: 'eating_detection', snakeNameUpper: 'EATING_DETECTION', pascalName: 'EatingDetection' },
    { snakeName: 'hard_braking', snakeNameUpper: 'HARD_BRAKING', pascalName: 'HardBraking' },
    { snakeName: 'hard_cornering', snakeNameUpper: 'HARD_CORNERING', pascalName: 'HardCornering' },
    { snakeName: 'idling', snakeNameUpper: 'IDLING', pascalName: 'Idling' },
    { snakeName: 'possible_impact', snakeNameUpper: 'POSSIBLE_IMPACT', pascalName: 'PossibleImpact' },
    { snakeName: 'potential_red_light_camera_violation', snakeNameUpper: 'POTENTIAL_RED_LIGHT_CAMERA_VIOLATION', pascalName: 'PotentialRedLightCameraViolation' },
    { snakeName: 'potential_speed_camera_violation', snakeNameUpper: 'POTENTIAL_SPEED_CAMERA_VIOLATION', pascalName: 'PotentialSpeedCameraViolation' },
    { snakeName: 'refueling', snakeNameUpper: 'REFUELING', pascalName: 'Refueling' },
    { snakeName: 'smoking_detection', snakeNameUpper: 'SMOKING_DETECTION', pascalName: 'SmokingDetection' },
    { snakeName: 'speeding', snakeNameUpper: 'SPEEDING', pascalName: 'Speeding' },
    { snakeName: 'stop_sign_violation', snakeNameUpper: 'STOP_SIGN_VIOLATION', pascalName: 'StopSignViolation' },
    { snakeName: 'tailgating_detection', snakeNameUpper: 'TAILGATING_DETECTION', pascalName: 'TailgatingDetection' },
    { snakeName: 'tired_detection', snakeNameUpper: 'TIRED_DETECTION', pascalName: 'TiredDetection' },
];
*/

// Redux Store Action Types (i.e., just their names)
export const ACTION_TYPES = {
    SET_GLOBAL_SETTINGS: "SET_GLOBAL_SETTINGS", // used when loading/refresh all settings
    RESET_GLOBAL_SETTINGS: "RESET_GLOBAL_SETTINGS", // used when loading/refresh all settings
    SET_AUDIO_AVAILABILITY: "SET_AUDIO_AVAILABILITY", // "AVAILABILITY" means ENABLED/DISABLED (avoiding "ENABLED" b/c it implies "TRUE" and boolean specificity)
    SET_STREAMING_VIDEO_AUDIO_AVAILABILITY: "SET_STREAMING_VIDEO_AUDIO_AVAILABILITY",
    SET_AUDIO_NOTIFICATIONS_AVAILABILITY: "SET_AUDIO_NOTIFICATIONS_AVAILABILITY",
    SET_CAMERA_CABIN_AVAILABILITY: "SET_CAMERA_CABIN_AVAILABILITY",
    SET_CAMERA_ROAD_AVAILABILITY: "SET_CAMERA_ROAD_AVAILABILITY",
    SET_VIDEO_RECORDING_PROFILE: "SET_VIDEO_RECORDING_PROFILE",
    SET_EVENTS_SPEEDING_AVAILABILITY: "SET_EVENTS_SPEEDING_AVAILABILITY",
    SET_EVENTS_SPEEDING_THRESHOLD: "SET_EVENTS_SPEEDING_THRESHOLD",
    SET_EVENTS_AGGRESSIVE_ACCEL_AVAILABILITY: "SET_EVENTS_AGGRESSIVE_ACCEL_AVAILABILITY",
    SET_EVENTS_AGGRESSIVE_ACCEL_THRESHOLD: "SET_EVENTS_AGGRESSIVE_ACCEL_THRESHOLD",
    SET_EVENTS_CAR_BUMPED_AVAILABILITY: "SET_EVENTS_CAR_BUMPED_AVAILABILITY",
    SET_EVENTS_CAR_BUMPED_THRESHOLD: "SET_EVENTS_CAR_BUMPED_THRESHOLD",
    SET_EVENTS_HARD_BRAKING_AVAILABILITY: "SET_EVENTS_HARD_BRAKING_AVAILABILITY",
    SET_EVENTS_HARD_BRAKING_THRESHOLD: "SET_EVENTS_HARD_BRAKING_THRESHOLD",
    SET_EVENTS_HARD_CORNERING_AVAILABILITY: "SET_EVENTS_HARD_CORNERING_AVAILABILITY",
    SET_EVENTS_HARD_CORNERING_THRESHOLD: "SET_EVENTS_HARD_CORNERING_THRESHOLD",
    SET_EVENTS_IDLING_AVAILABILITY: "SET_EVENTS_IDLING_AVAILABILITY",
    SET_EVENTS_IDLING_THRESHOLD: "SET_EVENTS_IDLING_THRESHOLD",
    SET_EVENTS_IDLING_SPEED_THRESHOLD: "SET_EVENTS_IDLING_SPEED_THRESHOLD",
    SET_EVENTS_POSSIBLE_IMPACT_AVAILABILITY: "SET_EVENTS_POSSIBLE_IMPACT_AVAILABILITY",
    SET_EVENTS_POSSIBLE_IMPACT_THRESHOLD: "SET_EVENTS_POSSIBLE_IMPACT_THRESHOLD",
    SET_EVENTS_TAILGATING_AVAILABILITY: "SET_EVENTS_TAILGATING_AVAILABILITY",
    SET_EVENTS_TAILGATING_SPEED_THRESHOLD: "SET_EVENTS_TAILGATING_SPEED_THRESHOLD",
    SET_EVENTS_TAILGATING_REACTION_TIME_THRESHOLD: "SET_EVENTS_TAILGATING_REACTION_TIME_THRESHOLD",
    SET_EVENTS_TAILGATING_FOLLOW_TIME_THRESHOLD: "SET_EVENTS_TAILGATING_FOLLOW_TIME_THRESHOLD",
    SET_ACCOUNT_DAILYFLEETREPORT_AVAILABILITY: "SET_ACCOUNT_DAILYFLEETREPORT_AVAILABILITY",
    SET_ACCOUNT_DAILYFLEETREPORT_TIMEZONE: "SET_ACCOUNT_DAILYFLEETREPORT_TIMEZONE",
    SET_ACCOUNT_DAILYFLEETREPORT_EMAILS: "SET_ACCOUNT_DAILYFLEETREPORT_EMAILS",
    SET_GESTURES_AVAILABILITY: "SET_GESTURES_AVAILABILITY",
    SET_INTEGRATIONS_FLEETIO_CREDENTIALS: "SET_INTEGRATIONS_FLEETIO_CREDENTIALS",
    SET_INTEGRATIONS_FLEETIO_CREDENTIALS_VERIFICATION: "SET_INTEGRATIONS_FLEETIO_CREDENTIALS_VERIFICATION", // Roughed in feature to be completed when "test" endpoint is available
    SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS: "SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS",
    SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS_VERIFICATION: "SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS_VERIFICATION", // Roughed in feature to be completed when "test" endpoint is available
    SET_UNIT_OF_MEASURE_DISTANCE: "SET_UNIT_OF_MEASURE_DISTANCE",
    SET_UNIT_OF_MEASURE_TEMPERATURE: "SET_UNIT_OF_MEASURE_TEMPERATURE",
    SET_WIFI_HOTSPOT_AVAILABILITY: "SET_WIFI_HOTSPOT_AVAILABILITY",
    SET_WIFI_HOTSPOT_CREDENTIALS: "SET_WIFI_HOTSPOT_CREDENTIALS",
    SET_FACIAL_RECOGNITION_AVAILABILITY: "SET_FACIAL_RECOGNITION_AVAILABILITY",
    ...events.reduce((acc, event) => {
      acc["SET_EVENTS_" + event.snakeNameUpper + "_AVAILABILITY"] = "SET_EVENTS_" + event.snakeNameUpper + "_AVAILABILITY";
      return acc;
    }, {}),
    // Audio alert action types for all events (hard-coded + generic)
    ...ALL_EVENT_NAMES.reduce((acc, event) => {
      acc["SET_EVENTS_" + event.snakeNameUpper + "_AUDIO_ALERT"] = "SET_EVENTS_" + event.snakeNameUpper + "_AUDIO_ALERT";
      return acc;
    }, {}),
    // Visual alert action types for all events (hard-coded + generic)
    ...ALL_EVENT_NAMES.reduce((acc, event) => {
      acc["SET_EVENTS_" + event.snakeNameUpper + "_VISUAL_ALERT"] = "SET_EVENTS_" + event.snakeNameUpper + "_VISUAL_ALERT";
      return acc;
    }, {})
}

const globalSettingsInitialState = { // used during init and RESET_GLOBAL_SETTINGS

    globalSettingsPending: true,
    globalSettingsLoaded: false,
    globalSettingsErrorMessage: undefined,

    // Audio settings
    cameraAudioPending: true, // I'm not convinced by the nested objects' value proposition (in redux!) for these basic items
    cameraAudioEnabled: undefined,
    cameraAudioErrorMessage: undefined,

    streamingVideoAudioPending: true,
    streamingVideoAudioEnabled: undefined,
    streamingVideoAudioErrorMessage: undefined,

    audioNotificationsPending: true,
    audioNotificationsEnabled: undefined,
    audioNotificationsErrorMessage: undefined,

    // Cameras settings
    cameraCabinPending: true,
    cameraCabinEnabled: undefined,
    cameraCabinErrorMessage: undefined,
    cameraRoadPending: true,
    cameraRoadEnabled: undefined,
    cameraRoadErrorMessage: undefined,
    videoRecordingProfilePending: true,
    videoRecordingProfile: undefined,
    videoRecordingProfileErrorMessage: undefined,

    // "./../" + Account.dailyFleetReport settings
    accountDailyFleetReportPending: true,
    accountDailyFleetReportEnabled: undefined,
    accountDailyFleetReportErrorMessage: undefined,
    accountDailyFleetReportTimezonePending: true,
    accountDailyFleetReportTimezone: undefined,
    accountDailyFleetReportTimezoneErrorMessage: undefined,
    accountDailyFleetReportEmailsPending: true,
    accountDailyFleetReportEmails: undefined,
    accountDailyFleetReportEmailsErrorMessage: undefined,

    // Events.Speeding settings
    eventsSpeedingPending: true,
    eventsSpeedingEnabled: undefined,
    eventsSpeedingErrorMessage: undefined,
    eventsSpeedingThresholdPending: true,
    eventsSpeedingThreshold: undefined,
    eventsSpeedingThresholdType: undefined,
    eventsSpeedingThresholdErrorMessage: undefined,

    // Events.Tailgating settings
    eventsTailgatingDetectionPending: true,
    eventsTailgatingDetectionEnabled: undefined,
    eventsTailgatingDetectionErrorMessage: undefined,
    eventsTailgatingDetectionSpeedThresholdPending: true,
    eventsTailgatingDetectionSpeedThreshold: undefined,
    eventsTailgatingDetectionSpeedThresholdErrorMessage: undefined,
    eventsTailgatingDetectionReactionTimeThresholdPending: true,
    eventsTailgatingDetectionReactionTimeThreshold: undefined,
    eventsTailgatingDetectionReactionTimeThresholdErrorMessage: undefined,
    eventsTailgatingDetectionFollowTimeThresholdPending: true,
    eventsTailgatingDetectionFollowTimeThreshold: undefined,
    eventsTailgatingDetectionFollowTimeThresholdType: undefined,
    eventsTailgatingDetectionFollowTimeThresholdErrorMessage: undefined,

    // Events.AggressiveAccel settings
    eventsAggressiveAccelPending: true,
    eventsAggressiveAccelEnabled: undefined,
    eventsAggressiveAccelErrorMessage: undefined,
    eventsAggressiveAccelThresholdPending: true,
    eventsAggressiveAccelThreshold: undefined,
    eventsAggressiveAccelThresholdErrorMessage: undefined,

    // Events.CarBumped settings
    eventsCarBumpedPending: true,
    eventsCarBumpedEnabled: undefined,
    eventsCarBumpedErrorMessage: undefined,
    eventsCarBumpedThresholdPending: true,
    eventsCarBumpedThreshold: undefined,
    eventsCarBumpedThresholdErrorMessage: undefined,
    // Events.HardBraking settings
    eventsHardBrakingPending: true,
    eventsHardBrakingEnabled: undefined,
    eventsHardBrakingErrorMessage: undefined,
    eventsHardBrakingThresholdPending: true,
    eventsHardBrakingThreshold: undefined,
    eventsHardBrakingThresholdErrorMessage: undefined,

    // Events.HardCornering settings
    eventsHardCorneringPending: true,
    eventsHardCorneringEnabled: undefined,
    eventsHardCorneringErrorMessage: undefined,
    eventsHardCorneringThresholdPending: true,
    eventsHardCorneringThreshold: undefined,
    eventsHardCorneringThresholdErrorMessage: undefined,

    // Events.Idling settings
    eventsIdlingPending: true,
    eventsIdlingEnabled: undefined,
    eventsIdlingErrorMessage: undefined,
    eventsIdlingThresholdPending: true,
    eventsIdlingThreshold: undefined,
    eventsIdlingThresholdErrorMessage: undefined,
    eventsIdlingSpeedThresholdPending: true,
    eventsIdlingSpeedThreshold: undefined,
    eventsIdlingSpeedThresholdErrorMessage: undefined,

    // Events.PossibleImpact settings
    eventsPossibleImpactPending: true,
    eventsPossibleImpactEnabled: undefined,
    eventsPossibleImpactErrorMessage: undefined,
    eventsPossibleImpactThresholdPending: true,
    eventsPossibleImpactThreshold: undefined,
    eventsPossibleImpactThresholdErrorMessage: undefined,

    // Gestures settings
    gesturesPending: true,
    gesturesEnabled: undefined,
    gesturesErrorMessage: undefined,

    // Integration settings
    // fleetio
    integrationsFleetioCredentialsPending: true,
    integrationsFleetioCredentialsAccountToken: undefined,
    integrationsFleetioCredentialsApiKey: undefined,
    integrationsFleetioCredentialsIntegrationId: undefined,
    integrationsFleetioCredentialsStatus: undefined,
    integrationsFleetioCredentialsStatusMessage: undefined,
    integrationsFleetioCredentialsErrorMessage: undefined,
    integrationsFleetioCredentialsVerificationPending: false, // verification not supported
    integrationsFleetioCredentialsVerificationIsValid: true, // show true (more positive feedback) while verification is pending
    integrationsFleetioCredentialsVerificationErrorMessage: undefined,

    // whiparound
    integrationsWhiparoundCredentialsPending: true,
    integrationsWhiparoundCredentialsApiKey: undefined,
    integrationsWhiparoundCredentialsIntegrationId: undefined,
    integrationsWhiparoundCredentialsStatus: undefined,
    integrationsWhiparoundCredentialsStatusMessage: undefined,
    integrationsWhiparoundCredentialsErrorMessage: undefined,
    integrationsWhiparoundCredentialsVerificationPending: false, // verification not supported
    integrationsWhiparoundCredentialsVerificationIsValid: true, // show true (more positive feedback) while verification is pending
    integrationsWhiparoundCredentialsVerificationErrorMessage: undefined,

    // Unit of Measure settings
    unitOfMeasureDistancePending: true,
    unitOfMeasureDistanceMetric: undefined,
    unitOfMeasureDistanceErrorMessage: undefined,
    unitOfMeasureTemperaturePending: true,
    unitOfMeasureTemperatureMetric: undefined,
    unitOfMeasureTemperatureErrorMessage: undefined,

    // WiFi settings
    wifiHotspotsPending: true,
    wifiHotspotsEnabled: undefined,
    wifiHotspotsErrorMessage: undefined,
    wifiHotspotsCredentialsPending: true,
    wifiHotspotsCredentialsSsid: undefined,
    wifiHotspotsCredentialsPassword: undefined,
    wifiHotspotsCredentialsErrorMessage: undefined,

    // Facial Recognition settings
    facialRecognitionPending: true,
    facialRecognitionEnabled: undefined,
    facialRecognitionErrorMessage: undefined,

    ...events.reduce((acc, event) => {
      acc["events" + event.pascalName + "Pending"] = true;
      acc["events" + event.pascalName + "Enabled"] = undefined;
      acc["events" + event.pascalName + "ErrorMessage"] = undefined;
      return acc;
    }, {}),

    // Audio alert state for all events (hard-coded + generic)
    ...ALL_EVENT_NAMES.reduce((acc, event) => {
      acc["events" + event.pascalName + "AudioAlertPending"] = true;
      acc["events" + event.pascalName + "AudioAlert"] = undefined;
      acc["events" + event.pascalName + "AudioAlertErrorMessage"] = undefined;
      return acc;
    }, {}),

    // Visual alert state for all events (hard-coded + generic)
    ...ALL_EVENT_NAMES.reduce((acc, event) => {
      acc["events" + event.pascalName + "VisualAlertPending"] = true;
      acc["events" + event.pascalName + "VisualAlert"] = undefined;
      acc["events" + event.pascalName + "VisualAlertErrorMessage"] = undefined;
      return acc;
    }, {})
}

// Redux Store Actions (i.e., functions that just return objects with specific "type" properties with ACTION_TYPES values and corresponding payloads with keys/values used to perform the ACTION_TYPE in the corresponding reducer)
export const ACTIONS = {

    updatingGlobalSettings: (isLoaded) => {
        return {
            type: ACTION_TYPES.SET_GLOBAL_SETTINGS,
            payload: {
                globalSettingsPending: true, // 'true' = the data is being loaded
                globalSettingsLoaded: isLoaded, // if "true", then refreshing
                globalSettingsErrorMessage: undefined
            }
        }
    },
    setGlobalSettings: (isLoaded, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_GLOBAL_SETTINGS,
            payload: {
                globalSettingsPending: false, // 'false' = the data is set
                globalSettingsLoaded: isLoaded,
                globalSettingsErrorMessage: errorMessage
            }
        }
    },
    resetGlobalSettings: () => {
        return {
            type: ACTION_TYPES.RESET_GLOBAL_SETTINGS
        }
    },


    updatingAudioAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_AUDIO_AVAILABILITY,
            payload: {
                cameraAudioPending: true, // 'true' = the update is in progress
                cameraAudioEnabled: isEnabled,
                cameraAudioErrorMessage: undefined
            }
        }
    },
    setAudioAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_AUDIO_AVAILABILITY,
            payload: {
                cameraAudioPending: false, // 'false' = the data is set
                cameraAudioEnabled: isEnabled,
                cameraAudioErrorMessage: errorMessage
            }
        }
    },

    updatingStreamingVideoAudioAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_STREAMING_VIDEO_AUDIO_AVAILABILITY,
            payload: {
                streamingVideoAudioPending: true, // 'true' = the update is in progress
                streamingVideoAudioEnabled: isEnabled,
                streamingVideoAudioErrorMessage: undefined
            }
        }
    },
    setStreamingVideoAudioAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_STREAMING_VIDEO_AUDIO_AVAILABILITY,
            payload: {
                streamingVideoAudioPending: false, // 'false' = the data is set
                streamingVideoAudioEnabled: isEnabled,
                streamingVideoAudioErrorMessage: errorMessage
            }
        }
    },

    updatingAudioNotificationsAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_AUDIO_NOTIFICATIONS_AVAILABILITY,
            payload: {
                audioNotificationsPending: true,
                audioNotificationsEnabled: isEnabled,
                audioNotificationsErrorMessage: undefined
            }
        }
    },
    setAudioNotificationsAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_AUDIO_NOTIFICATIONS_AVAILABILITY,
            payload: {
                audioNotificationsPending: false,
                audioNotificationsEnabled: isEnabled,
                audioNotificationsErrorMessage: errorMessage
            }
        }
    },

    updatingCameraCabinAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_CAMERA_CABIN_AVAILABILITY,
            payload: {
                cameraCabinPending: true,
                cameraCabinEnabled: isEnabled,
                cameraCabinErrorMessage: undefined
            }
        }
    },
    setCameraCabinAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_CAMERA_CABIN_AVAILABILITY,
            payload: {
                cameraCabinPending: false,
                cameraCabinEnabled: isEnabled,
                cameraCabinErrorMessage: errorMessage
            }
        }
    },


    updatingCameraRoadAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_CAMERA_ROAD_AVAILABILITY,
            payload: {
                cameraRoadPending: true,
                cameraRoadEnabled: isEnabled,
                cameraRoadErrorMessage: undefined
            }
        }
    },
    setCameraRoadAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_CAMERA_ROAD_AVAILABILITY,
            payload: {
                cameraRoadPending: false,
                cameraRoadEnabled: isEnabled,
                cameraRoadErrorMessage: errorMessage
            }
        }
    },


    updatingVideoRecordingProfile: (videoRecordingProfile) => {
        return {
            type: ACTION_TYPES.SET_VIDEO_RECORDING_PROFILE,
            payload: {
                videoRecordingProfilePending: true,
                videoRecordingProfile: videoRecordingProfile,
                videoRecordingProfileErrorMessage: undefined
            }
        }
    },
    setVideoRecordingProfile: (videoRecordingProfile, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_VIDEO_RECORDING_PROFILE,
            payload: {
                videoRecordingProfilePending: false,
                videoRecordingProfile: videoRecordingProfile,
                videoRecordingProfileErrorMessage: errorMessage
            }
        }
    },


    updatingEventsSpeedingAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_SPEEDING_AVAILABILITY,
            payload: {
                eventsSpeedingPending: true,
                eventsSpeedingEnabled: isEnabled,
                eventsSpeedingErrorMessage: undefined
            }
        }
    },
    setEventsSpeedingAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_SPEEDING_AVAILABILITY,
            payload: {
                eventsSpeedingPending: false,
                eventsSpeedingEnabled: isEnabled,
                eventsSpeedingErrorMessage: errorMessage
            }
        }
    },


    updatingEventsSpeedingThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_SPEEDING_THRESHOLD,
            payload: {
                eventsSpeedingThresholdPending: true,
                eventsSpeedingThreshold: threshold,
                eventsSpeedingThresholdType: thresholdType,
                eventsSpeedingErrorMessage: undefined
            }
        }
    },
    setEventsSpeedingThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_SPEEDING_THRESHOLD,
            payload: {
                eventsSpeedingThresholdPending: false,
                eventsSpeedingThreshold: threshold,
                eventsSpeedingThresholdType: thresholdType,
                eventsSpeedingErrorMessage: errorMessage
            }
        }
    },
    
    updatingEventsTailgatingDetectionAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_AVAILABILITY,
            payload: {
                eventsTailgatingDetectionPending: true,
                eventsTailgatingDetectionEnabled: isEnabled,
                eventsTailgatingDetectionErrorMessage: undefined
            }
        }
    },
    setEventsTailgatingDetectionAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_AVAILABILITY,
            payload: {
                eventsTailgatingDetectionPending: false,
                eventsTailgatingDetectionEnabled: isEnabled,
                eventsTailgatingDetectionErrorMessage: errorMessage
            }
        }
    },


    updatingEventsTailgatingDetectionSpeedThreshold: (threshold) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_SPEED_THRESHOLD,
            payload: {
                eventsTailgatingDetectionSpeedThresholdPending: true,
                eventsTailgatingDetectionSpeedThreshold: threshold,
                eventsTailgatingDetectionSpeedErrorMessage: undefined
            }
        }
    },
    setEventsTailgatingDetectionSpeedThreshold: (threshold, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_SPEED_THRESHOLD,
            payload: {
                eventsTailgatingDetectionSpeedThresholdPending: false,
                eventsTailgatingDetectionSpeedThreshold: threshold,
                eventsTailgatingDetectionSpeedErrorMessage: errorMessage
            }
        }
    },

    updatingEventsTailgatingDetectionReactionTimeThreshold: (threshold) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_REACTION_TIME_THRESHOLD,
            payload: {
                eventsTailgatingDetectionReactionTimeThresholdPending: true,
                eventsTailgatingDetectionReactionTimeThreshold: threshold,
                eventsTailgatingDetectionReactionTimeErrorMessage: undefined
            }
        }
    },
    setEventsTailgatingDetectionReactionTimeThreshold: (threshold, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_REACTION_TIME_THRESHOLD,
            payload: {
                eventsTailgatingDetectionReactionTimeThresholdPending: false,
                eventsTailgatingDetectionReactionTimeThreshold: threshold,
                eventsTailgatingDetectionReactionTimeErrorMessage: errorMessage
            }
        }
    },

    updatingEventsTailgatingDetectionFollowTimeThreshold: (threshold) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_FOLLOW_TIME_THRESHOLD,
            payload: {
                eventsTailgatingDetectionFollowTimeThresholdPending: true,
                eventsTailgatingDetectionFollowTimeThreshold: threshold,
                eventsTailgatingDetectionFollowTimeErrorMessage: undefined
            }
        }
    },
    setEventsTailgatingDetectionFollowTimeThreshold: (threshold, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_TAILGATING_FOLLOW_TIME_THRESHOLD,
            payload: {
                eventsTailgatingDetectionFollowTimeThresholdPending: false,
                eventsTailgatingDetectionFollowTimeThreshold: threshold,
                eventsTailgatingDetectionFollowTimeErrorMessage: errorMessage
            }
        }
    },

    updatingEventsAggressiveAccelAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_AGGRESSIVE_ACCEL_AVAILABILITY,
            payload: {
                eventsAggressiveAccelPending: true,
                eventsAggressiveAccelEnabled: isEnabled,
                eventsAggressiveAccelErrorMessage: undefined
            }
        }
    },
    setEventsAggressiveAccelAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_AGGRESSIVE_ACCEL_AVAILABILITY,
            payload: {
                eventsAggressiveAccelPending: false,
                eventsAggressiveAccelEnabled: isEnabled,
                eventsAggressiveAccelErrorMessage: errorMessage
            }
        }
    },


    updatingEventsAggressiveAccelThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_AGGRESSIVE_ACCEL_THRESHOLD,
            payload: {
                eventsAggressiveAccelThresholdPending: true,
                eventsAggressiveAccelThreshold: threshold,
                eventsAggressiveAccelThresholdType: thresholdType,
                eventsAggressiveAccelErrorMessage: undefined
            }
        }
    },
    setEventsAggressiveAccelThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_AGGRESSIVE_ACCEL_THRESHOLD,
            payload: {
                eventsAggressiveAccelThresholdPending: false,
                eventsAggressiveAccelThreshold: threshold,
                eventsAggressiveAccelThresholdType: thresholdType,
                eventsAggressiveAccelErrorMessage: errorMessage
            }
        }
    },


    updatingEventsCarBumpedAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_CAR_BUMPED_AVAILABILITY,
            payload: {
                eventsCarBumpedPending: true,
                eventsCarBumpedEnabled: isEnabled,
                eventsCarBumpedErrorMessage: undefined
            }
        }
    },
    setEventsCarBumpedAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_CAR_BUMPED_AVAILABILITY,
            payload: {
                eventsCarBumpedPending: false,
                eventsCarBumpedEnabled: isEnabled,
                eventsCarBumpedErrorMessage: errorMessage
            }
        }
    },


    updatingEventsCarBumpedThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_CAR_BUMPED_THRESHOLD,
            payload: {
                eventsCarBumpedThresholdPending: true,
                eventsCarBumpedThreshold: threshold,
                eventsCarBumpedThresholdType: thresholdType,
                eventsCarBumpedErrorMessage: undefined
            }
        }
    },
    setEventsCarBumpedThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_CAR_BUMPED_THRESHOLD,
            payload: {
                eventsCarBumpedThresholdPending: false,
                eventsCarBumpedThreshold: threshold,
                eventsCarBumpedThresholdType: thresholdType,
                eventsCarBumpedErrorMessage: errorMessage
            }
        }
    },


    ...events.reduce((acc, event) => {
      acc["updatingEvents" + event.pascalName + "Availability"] = (isEnabled) => {
        return {
            type: ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_AVAILABILITY"],
            payload: {
                ["events" + event.pascalName + "Pending"]: true,
                ["events" + event.pascalName + "Enabled"]: isEnabled,
                ["events" + event.pascalName + "ErrorMessage"]: undefined
            }
        }
      }
      acc["setEvents" + event.pascalName + "Availability"] = (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_AVAILABILITY"],
            payload: {
                ["events" + event.pascalName + "Pending"]: false,
                ["events" + event.pascalName + "Enabled"]: isEnabled,
                ["events" + event.pascalName + "ErrorMessage"]: errorMessage
            }
        }
      }
      return acc;
    }, {}),

    // Audio alert action creators for all events (hard-coded + generic)
    ...ALL_EVENT_NAMES.reduce((acc, event) => {
      acc["updatingEvents" + event.pascalName + "AudioAlert"] = (audioAlertType) => {
        return {
            type: ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_AUDIO_ALERT"],
            payload: {
                ["events" + event.pascalName + "AudioAlertPending"]: true,
                ["events" + event.pascalName + "AudioAlert"]: audioAlertType,
                ["events" + event.pascalName + "AudioAlertErrorMessage"]: undefined
            }
        }
      }
      acc["setEvents" + event.pascalName + "AudioAlert"] = (audioAlertType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_AUDIO_ALERT"],
            payload: {
                ["events" + event.pascalName + "AudioAlertPending"]: false,
                ["events" + event.pascalName + "AudioAlert"]: audioAlertType,
                ["events" + event.pascalName + "AudioAlertErrorMessage"]: errorMessage
            }
        }
      }
      return acc;
    }, {}),

    // Visual alert action creators for all events (hard-coded + generic)
    ...ALL_EVENT_NAMES.reduce((acc, event) => {
      acc["updatingEvents" + event.pascalName + "VisualAlert"] = (isEnabled) => {
        return {
            type: ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_VISUAL_ALERT"],
            payload: {
                ["events" + event.pascalName + "VisualAlertPending"]: true,
                ["events" + event.pascalName + "VisualAlert"]: isEnabled,
                ["events" + event.pascalName + "VisualAlertErrorMessage"]: undefined
            }
        }
      }
      acc["setEvents" + event.pascalName + "VisualAlert"] = (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_VISUAL_ALERT"],
            payload: {
                ["events" + event.pascalName + "VisualAlertPending"]: false,
                ["events" + event.pascalName + "VisualAlert"]: isEnabled,
                ["events" + event.pascalName + "VisualAlertErrorMessage"]: errorMessage
            }
        }
      }
      return acc;
    }, {}),

    updatingEventsHardBrakingAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_BRAKING_AVAILABILITY,
            payload: {
                eventsHardBrakingPending: true,
                eventsHardBrakingEnabled: isEnabled,
                eventsHardBrakingErrorMessage: undefined
            }
        }
    },
    setEventsHardBrakingAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_BRAKING_AVAILABILITY,
            payload: {
                eventsHardBrakingPending: false,
                eventsHardBrakingEnabled: isEnabled,
                eventsHardBrakingErrorMessage: errorMessage
            }
        }
    },


    updatingEventsHardBrakingThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_BRAKING_THRESHOLD,
            payload: {
                eventsHardBrakingThresholdPending: true,
                eventsHardBrakingThreshold: threshold,
                eventsHardBrakingThresholdType: thresholdType,
                eventsHardBrakingErrorMessage: undefined
            }
        }
    },
    setEventsHardBrakingThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_BRAKING_THRESHOLD,
            payload: {
                eventsHardBrakingThresholdPending: false,
                eventsHardBrakingThreshold: threshold,
                eventsHardBrakingThresholdType: thresholdType,
                eventsHardBrakingErrorMessage: errorMessage
            }
        }
    },


    updatingEventsHardCorneringAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_CORNERING_AVAILABILITY,
            payload: {
                eventsHardCorneringPending: true,
                eventsHardCorneringEnabled: isEnabled,
                eventsHardCorneringErrorMessage: undefined
            }
        }
    },
    setEventsHardCorneringAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_CORNERING_AVAILABILITY,
            payload: {
                eventsHardCorneringPending: false,
                eventsHardCorneringEnabled: isEnabled,
                eventsHardCorneringErrorMessage: errorMessage
            }
        }
    },


    updatingEventsHardCorneringThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_CORNERING_THRESHOLD,
            payload: {
                eventsHardCorneringThresholdPending: true,
                eventsHardCorneringThreshold: threshold,
                eventsHardCorneringThresholdType: thresholdType,
                eventsHardCorneringErrorMessage: undefined
            }
        }
    },
    setEventsHardCorneringThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_HARD_CORNERING_THRESHOLD,
            payload: {
                eventsHardCorneringThresholdPending: false,
                eventsHardCorneringThreshold: threshold,
                eventsHardCorneringThresholdType: thresholdType,
                eventsHardCorneringErrorMessage: errorMessage
            }
        }
    },


    updatingEventsIdlingAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_IDLING_AVAILABILITY,
            payload: {
                eventsIdlingPending: true,
                eventsIdlingEnabled: isEnabled,
                eventsIdlingErrorMessage: undefined
            }
        }
    },
    setEventsIdlingAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_IDLING_AVAILABILITY,
            payload: {
                eventsIdlingPending: false,
                eventsIdlingEnabled: isEnabled,
                eventsIdlingErrorMessage: errorMessage
            }
        }
    },


    updatingEventsIdlingThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_IDLING_THRESHOLD,
            payload: {
                eventsIdlingThresholdPending: true,
                eventsIdlingThreshold: threshold,
                eventsIdlingThresholdType: thresholdType,
                eventsIdlingErrorMessage: undefined
            }
        }
    },
    setEventsIdlingThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_IDLING_THRESHOLD,
            payload: {
                eventsIdlingThresholdPending: false,
                eventsIdlingThreshold: threshold,
                eventsIdlingThresholdType: thresholdType,
                eventsIdlingErrorMessage: errorMessage
            }
        }
    },

    updatingEventsIdlingSpeedThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_IDLING_SPEED_THRESHOLD,
            payload: {
                eventsIdlingSpeedThresholdPending: true,
                eventsIdlingSpeedThreshold: threshold,
                eventsIdlingSpeedThresholdType: thresholdType,
                eventsIdlingSpeedErrorMessage: undefined
            }
        }
    },
    setEventsIdlingSpeedThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_IDLING_SPEED_THRESHOLD,
            payload: {
                eventsIdlingSpeedThresholdPending: false,
                eventsIdlingSpeedThreshold: threshold,
                eventsIdlingSpeedThresholdType: thresholdType,
                eventsIdlingSpeedErrorMessage: errorMessage
            }
        }
    },


    updatingEventsPossibleImpactAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_POSSIBLE_IMPACT_AVAILABILITY,
            payload: {
                eventsPossibleImpactPending: true,
                eventsPossibleImpactEnabled: isEnabled,
                eventsPossibleImpactErrorMessage: undefined
            }
        }
    },
    setEventsPossibleImpactAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_POSSIBLE_IMPACT_AVAILABILITY,
            payload: {
                eventsPossibleImpactPending: false,
                eventsPossibleImpactEnabled: isEnabled,
                eventsPossibleImpactErrorMessage: errorMessage
            }
        }
    },


    updatingEventsPossibleImpactThreshold: (threshold, thresholdType) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_POSSIBLE_IMPACT_THRESHOLD,
            payload: {
                eventsPossibleImpactThresholdPending: true,
                eventsPossibleImpactThreshold: threshold,
                eventsPossibleImpactThresholdType: thresholdType,
                eventsPossibleImpactErrorMessage: undefined
            }
        }
    },
    setEventsPossibleImpactThreshold: (threshold, thresholdType, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENTS_POSSIBLE_IMPACT_THRESHOLD,
            payload: {
                eventsPossibleImpactThresholdPending: false,
                eventsPossibleImpactThreshold: threshold,
                eventsPossibleImpactThresholdType: thresholdType,
                eventsPossibleImpactErrorMessage: errorMessage
            }
        }
    },


    updatingAccountDailyFleetReportAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_AVAILABILITY,
            payload: {
                accountDailyFleetReportPending: true,
                accountDailyFleetReportEnabled: isEnabled,
                accountDailyFleetReportErrorMessage: undefined
            }
        }
    },
    setAccountDailyFleetReportAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_AVAILABILITY,
            payload: {
                accountDailyFleetReportPending: false,
                accountDailyFleetReportEnabled: isEnabled,
                accountDailyFleetReportErrorMessage: errorMessage
            }
        }
    },

    updatingAccountDailyFleetReportTimezone: (timezone) => {
        return {
            type: ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_TIMEZONE,
            payload: {
                accountDailyFleetReportTimezonePending: true,
                accountDailyFleetReportTimezone: timezone,
                accountDailyFleetReportTimezoneErrorMessage: undefined
            }
        }
    },
    setAccountDailyFleetReportTimezone: (timezone, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_TIMEZONE,
            payload: {
                accountDailyFleetReportTimezonePending: false,
                accountDailyFleetReportTimezone: timezone,
                accountDailyFleetReportTimezoneErrorMessage: errorMessage
            }
        }
    },

    updatingAccountDailyFleetReportEmails: (emailsArray) => {
        return {
            type: ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_EMAILS,
            payload: {
                accountDailyFleetReportEmailsPending: true,
                accountDailyFleetReportEmails: emailsArray,
                accountDailyFleetReportEmailsErrorMessage: undefined
            }
        }
    },
    setAccountDailyFleetReportEmails: (emailsArray, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_EMAILS,
            payload: {
                accountDailyFleetReportEmailsPending: false,
                accountDailyFleetReportEmails: emailsArray,
                accountDailyFleetReportEmailsErrorMessage: errorMessage
            }
        }
    },


    updatingGesturesAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_GESTURES_AVAILABILITY,
            payload: {
                gesturesPending: true,
                gesturesEnabled: isEnabled,
                gesturesErrorMessage: undefined
            }
        }
    },
    setGesturesAvailability: (isEnabled, errorMessage = undefined) => {
        console.log("setGesturesAvailability: ", isEnabled);
        return {
            type: ACTION_TYPES.SET_GESTURES_AVAILABILITY,
            payload: {
                gesturesPending: false,
                gesturesEnabled: isEnabled,
                gesturesErrorMessage: errorMessage
            }
        }
    },



    updatingIntegrationsFleetioCredentials: (accountToken, apiKey, integrationId = undefined) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_FLEETIO_CREDENTIALS,
            payload: {
                integrationsFleetioCredentialsPending: true,
                integrationsFleetioCredentialsAccountToken: accountToken,
                integrationsFleetioCredentialsApiKey: apiKey,
                integrationsFleetioCredentialsIntegrationId: integrationId,
                integrationsFleetioCredentialsStatus: undefined,
                integrationsFleetioCredentialsStatusMessage: undefined,
                integrationsFleetioCredentialsErrorMessage: undefined
            }
        }
    },
    setIntegrationsFleetioCredentials: (accountToken, apiKey, integrationId = undefined, stat = undefined, statusMessage = undefined, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_FLEETIO_CREDENTIALS,
            payload: {
                integrationsFleetioCredentialsPending: false,
                integrationsFleetioCredentialsAccountToken: accountToken,
                integrationsFleetioCredentialsApiKey: apiKey,
                integrationsFleetioCredentialsIntegrationId: integrationId,
                integrationsFleetioCredentialsStatus: stat,
                integrationsFleetioCredentialsStatusMessage: statusMessage,
                integrationsFleetioCredentialsErrorMessage: errorMessage
            }
        }
    },

    updatingIntegrationsFleetioCredentialsVerification: (isValid) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_FLEETIO_CREDENTIALS,
            payload: {
                integrationsFleetioCredentialsVerificationPending: true,
                integrationsFleetioCredentialsVerificationIsValid: isValid,
                integrationsFleetioCredentialsVerificationErrorMessage: undefined
            }
        }
    },
    setIntegrationsFleetioCredentialsVerification: (isValid, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_FLEETIO_CREDENTIALS,
            payload: {
                integrationsFleetioCredentialsVerificationPending: false,
                integrationsFleetioCredentialsVerificationIsValid: isValid,
                integrationsFleetioCredentialsVerificationErrorMessage: errorMessage
            }
        }
    },


    updatingIntegrationsWhiparoundCredentials: (apiKey, integrationId = undefined) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS,
            payload: {
                integrationsWhiparoundCredentialsPending: true,
                integrationsWhiparoundCredentialsApiKey: apiKey,
                integrationsWhiparoundCredentialsIntegrationId: integrationId,
                integrationsWhiparoundCredentialsStatus: undefined,
                integrationsWhiparoundCredentialsStatusMessage: undefined,
                integrationsWhiparoundCredentialsErrorMessage: undefined
            }
        }
    },
    setIntegrationsWhiparoundCredentials: (apiKey, integrationId = undefined, stat = undefined, statusMessage = undefined, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS,
            payload: {
                integrationsWhiparoundCredentialsPending: false,
                integrationsWhiparoundCredentialsApiKey: apiKey,
                integrationsWhiparoundCredentialsIntegrationId: integrationId,
                integrationsWhiparoundCredentialsStatus: stat,
                integrationsWhiparoundCredentialsStatusMessage: statusMessage,
                integrationsWhiparoundCredentialsErrorMessage: errorMessage
            }
        }
    },

    updatingIntegrationsWhiparoundCredentialsVerification: (isValid) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS,
            payload: {
                integrationsWhiparoundCredentialsVerificationPending: true,
                integrationsWhiparoundCredentialsVerificationIsValid: isValid,
                integrationsWhiparoundCredentialsVerificationErrorMessage: undefined
            }
        }
    },
    setIntegrationsWhiparoundCredentialsVerification: (isValid, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS,
            payload: {
                integrationsWhiparoundCredentialsVerificationPending: false,
                integrationsWhiparoundCredentialsVerificationIsValid: isValid,
                integrationsWhiparoundCredentialsVerificationErrorMessage: errorMessage
            }
        }
    },


    updatingUnitOfMeasureDistance: (isMetric) => {
        return {
            type: ACTION_TYPES.SET_UNIT_OF_MEASURE_DISTANCE,
            payload: {
                unitOfMeasureDistancePending: true,
                unitOfMeasureDistanceMetric: isMetric,
                unitOfMeasureDistanceErrorMessage: undefined
            }
        }
    },
    setUnitOfMeasureDistance: (isMetric, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_UNIT_OF_MEASURE_DISTANCE,
            payload: {
                unitOfMeasureDistancePending: false,
                unitOfMeasureDistanceMetric: isMetric,
                unitOfMeasureDistanceErrorMessage: errorMessage
            }
        }
    },



    updatingUnitOfMeasureTemperature: (isMetric) => {
        return {
            type: ACTION_TYPES.SET_UNIT_OF_MEASURE_TEMPERATURE,
            payload: {
                unitOfMeasureTemperaturePending: true,
                unitOfMeasureTemperatureMetric: isMetric,
                unitOfMeasureTemperatureErrorMessage: undefined
            }
        }
    },
    setUnitOfMeasureTemperature: (isMetric, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_UNIT_OF_MEASURE_TEMPERATURE,
            payload: {
                unitOfMeasureTemperaturePending: false,
                unitOfMeasureTemperatureMetric: isMetric,
                unitOfMeasureTemperatureErrorMessage: errorMessage
            }
        }
    },



    updatingWiFiHotspotAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_WIFI_HOTSPOT_AVAILABILITY,
            payload: {
                wifiHotspotsPending: true,
                wifiHotspotsEnabled: isEnabled,
                wifiHotspotsErrorMessage: undefined
            }
        }
    },
    setWiFiHotspotAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_WIFI_HOTSPOT_AVAILABILITY,
            payload: {
                wifiHotspotsPending: false,
                wifiHotspotsEnabled: isEnabled,
                wifiHotspotsErrorMessage: errorMessage
            }
        }
    },



    updatingWiFiHotspotCredentials: (ssid, password) => {
        return {
            type: ACTION_TYPES.SET_WIFI_HOTSPOT_CREDENTIALS,
            payload: {
                wifiHotspotsCredentialsPending: true,
                wifiHotspotsCredentialsSsid: ssid,
                wifiHotspotsCredentialsPassword: password,
                wifiHotspotsCredentialsErrorMessage: undefined
            }
        }
    },
    setWiFiHotspotCredentials: (ssid, password, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_WIFI_HOTSPOT_CREDENTIALS,
            payload: {
                wifiHotspotsCredentialsPending: false,
                wifiHotspotsCredentialsSsid: ssid,
                wifiHotspotsCredentialsPassword: password,
                wifiHotspotsCredentialsErrorMessage: errorMessage
            }
        }
    },


    updatingFacialRecognitionAvailability: (isEnabled) => {
        return {
            type: ACTION_TYPES.SET_FACIAL_RECOGNITION_AVAILABILITY,
            payload: {
                facialRecognitionPending: true,
                facialRecognitionEnabled: isEnabled,
                facialRecognitionErrorMessage: undefined
            }
        }
    },
    setFacialRecognitionAvailability: (isEnabled, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_FACIAL_RECOGNITION_AVAILABILITY,
            payload: {
                facialRecognitionPending: false,
                facialRecognitionEnabled: isEnabled,
                facialRecognitionErrorMessage: errorMessage
            }
        }
    }
}

export const globalSettingsReducer = (state = globalSettingsInitialState, action) => { // reminder: "initial state" must be returned as default case below
  
  for (let event of events) {
    if (action.type === ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_AVAILABILITY"]) {
      return {
        ...state,
        ["events" + event.pascalName + "Pending"]: action.payload["events" + event.pascalName + "Pending"],
        ["events" + event.pascalName + "Enabled"]: action.payload["events" + event.pascalName + "Enabled"],
        ["events" + event.pascalName + "ErrorMessage"]: action.payload["events" + event.pascalName + "ErrorMessage"]
      }
    }
  }

  // Audio alert reducer for all events (hard-coded + generic)
  for (let event of ALL_EVENT_NAMES) {
    if (action.type === ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_AUDIO_ALERT"]) {
      return {
        ...state,
        ["events" + event.pascalName + "AudioAlertPending"]: action.payload["events" + event.pascalName + "AudioAlertPending"],
        ["events" + event.pascalName + "AudioAlert"]: action.payload["events" + event.pascalName + "AudioAlert"],
        ["events" + event.pascalName + "AudioAlertErrorMessage"]: action.payload["events" + event.pascalName + "AudioAlertErrorMessage"]
      }
    }
  }

  // Visual alert reducer for all events (hard-coded + generic)
  for (let event of ALL_EVENT_NAMES) {
    if (action.type === ACTION_TYPES["SET_EVENTS_" + event.snakeNameUpper + "_VISUAL_ALERT"]) {
      return {
        ...state,
        ["events" + event.pascalName + "VisualAlertPending"]: action.payload["events" + event.pascalName + "VisualAlertPending"],
        ["events" + event.pascalName + "VisualAlert"]: action.payload["events" + event.pascalName + "VisualAlert"],
        ["events" + event.pascalName + "VisualAlertErrorMessage"]: action.payload["events" + event.pascalName + "VisualAlertErrorMessage"]
      }
    }
  }
  
  switch(action.type) {
        case ACTION_TYPES.SET_GLOBAL_SETTINGS:
            // IMPORTANT: state is a ref(?), do not edit directly and then return
            return { // NEW state object
                ...state,
                globalSettingsPending: action.payload.globalSettingsPending,
                globalSettingsEnabled: action.payload.globalSettingsEnabled,
                globalSettingsErrorMessage: action.payload.globalSettingsErrorMessage
            };
        case ACTION_TYPES.RESET_GLOBAL_SETTINGS:
            return globalSettingsInitialState;
        case ACTION_TYPES.SET_AUDIO_AVAILABILITY:
            // IMPORTANT: state is a ref(?), do not edit directly and then return
            return { // NEW state object
                ...state,
                cameraAudioPending: action.payload.cameraAudioPending,
                cameraAudioEnabled: action.payload.cameraAudioEnabled,
                cameraAudioErrorMessage: action.payload.cameraAudioErrorMessage
            };
        case ACTION_TYPES.SET_STREAMING_VIDEO_AUDIO_AVAILABILITY:
            return {
                ...state,
                streamingVideoAudioPending: action.payload.streamingVideoAudioPending,
                streamingVideoAudioEnabled: action.payload.streamingVideoAudioEnabled,
                streamingVideoAudioErrorMessage: action.payload.streamingVideoAudioErrorMessage
            };
        case ACTION_TYPES.SET_AUDIO_NOTIFICATIONS_AVAILABILITY:
            return {
                ...state,
                audioNotificationsPending: action.payload.audioNotificationsPending,
                audioNotificationsEnabled: action.payload.audioNotificationsEnabled,
                audioNotificationsErrorMessage: action.payload.audioNotificationsErrorMessage
            }
        case ACTION_TYPES.SET_CAMERA_CABIN_AVAILABILITY:
            return {
                ...state,
                cameraCabinPending: action.payload.cameraCabinPending,
                cameraCabinEnabled: action.payload.cameraCabinEnabled,
                cameraCabinErrorMessage: action.payload.cameraCabinErrorMessage
            }
        case ACTION_TYPES.SET_CAMERA_ROAD_AVAILABILITY:
            return {
                ...state,
                cameraRoadPending: action.payload.cameraRoadPending,
                cameraRoadEnabled: action.payload.cameraRoadEnabled,
                cameraRoadErrorMessage: action.payload.cameraRoadErrorMessage
            }
        case ACTION_TYPES.SET_VIDEO_RECORDING_PROFILE:
            return {
                ...state,
                videoRecordingProfilePending: action.payload.videoRecordingProfilePending,
                videoRecordingProfile: action.payload.videoRecordingProfile,
                videoRecordingProfileErrorMessage: action.payload.videoRecordingProfileErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_SPEEDING_AVAILABILITY:
            return {
                ...state,
                eventsSpeedingPending: action.payload.eventsSpeedingPending,
                eventsSpeedingEnabled: action.payload.eventsSpeedingEnabled,
                eventsSpeedingErrorMessage: action.payload.eventsSpeedingErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_SPEEDING_THRESHOLD:
            return {
                ...state,
                eventsSpeedingThresholdPending: action.payload.eventsSpeedingThresholdPending,
                eventsSpeedingThreshold: action.payload.eventsSpeedingThreshold,
                eventsSpeedingThresholdType: action.payload.eventsSpeedingThresholdType,
                eventsSpeedingThresholdErrorMessage: action.payload.eventsSpeedingThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_AGGRESSIVE_ACCEL_AVAILABILITY:
            return {
                ...state,
                eventsAggressiveAccelPending: action.payload.eventsAggressiveAccelPending,
                eventsAggressiveAccelEnabled: action.payload.eventsAggressiveAccelEnabled,
                eventsAggressiveAccelErrorMessage: action.payload.eventsAggressiveAccelErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_AGGRESSIVE_ACCEL_THRESHOLD:
            return {
                ...state,
                eventsAggressiveAccelThresholdPending: action.payload.eventsAggressiveAccelThresholdPending,
                eventsAggressiveAccelThreshold: action.payload.eventsAggressiveAccelThreshold,
                eventsAggressiveAccelThresholdErrorMessage: action.payload.eventsAggressiveAccelThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_TAILGATING_AVAILABILITY:
            return {
                ...state,
                eventsTailgatingDetectionPending: action.payload.eventsTailgatingDetectionPending,
                eventsTailgatingDetectionEnabled: action.payload.eventsTailgatingDetectionEnabled,
                eventsTailgatingDetectionErrorMessage: action.payload.eventsTailgatingDetectionErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_TAILGATING_SPEED_THRESHOLD:
            return {
                ...state,
                eventsTailgatingDetectionSpeedThresholdPending: action.payload.eventsTailgatingDetectionSpeedThresholdPending,
                eventsTailgatingDetectionSpeedThreshold: action.payload.eventsTailgatingDetectionSpeedThreshold,
                eventsTailgatingDetectionSpeedThresholdErrorMessage: action.payload.eventsTailgatingDetectionSpeedThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_TAILGATING_REACTION_TIME_THRESHOLD:
            return {
                ...state,
                eventsTailgatingDetectionReactionTimeThresholdPending: action.payload.eventsTailgatingDetectionReactionTimeThresholdPending,
                eventsTailgatingDetectionReactionTimeThreshold: action.payload.eventsTailgatingDetectionReactionTimeThreshold,
                eventsTailgatingDetectionReactionTimeThresholdErrorMessage: action.payload.eventsTailgatingDetectionReactionTimeThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_TAILGATING_FOLLOW_TIME_THRESHOLD:
            return {
                ...state,
                eventsTailgatingDetectionFollowTimeThresholdPending: action.payload.eventsTailgatingDetectionFollowTimeThresholdPending,
                eventsTailgatingDetectionFollowTimeThreshold: action.payload.eventsTailgatingDetectionFollowTimeThreshold,
                eventsTailgatingDetectionFollowTimeThresholdErrorMessage: action.payload.eventsTailgatingDetectionFollowTimeThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_CAR_BUMPED_AVAILABILITY:
            return {
                ...state,
                eventsCarBumpedPending: action.payload.eventsCarBumpedPending,
                eventsCarBumpedEnabled: action.payload.eventsCarBumpedEnabled,
                eventsCarBumpedErrorMessage: action.payload.eventsCarBumpedErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_CAR_BUMPED_THRESHOLD:
            return {
                ...state,
                eventsCarBumpedThresholdPending: action.payload.eventsCarBumpedThresholdPending,
                eventsCarBumpedThreshold: action.payload.eventsCarBumpedThreshold,
                eventsCarBumpedThresholdErrorMessage: action.payload.eventsCarBumpedThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_HARD_BRAKING_AVAILABILITY:
            return {
                ...state,
                eventsHardBrakingPending: action.payload.eventsHardBrakingPending,
                eventsHardBrakingEnabled: action.payload.eventsHardBrakingEnabled,
                eventsHardBrakingErrorMessage: action.payload.eventsHardBrakingErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_HARD_BRAKING_THRESHOLD:
            return {
                ...state,
                eventsHardBrakingThresholdPending: action.payload.eventsHardBrakingThresholdPending,
                eventsHardBrakingThreshold: action.payload.eventsHardBrakingThreshold,
                eventsHardBrakingThresholdErrorMessage: action.payload.eventsHardBrakingThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_HARD_CORNERING_AVAILABILITY:
            return {
                ...state,
                eventsHardCorneringPending: action.payload.eventsHardCorneringPending,
                eventsHardCorneringEnabled: action.payload.eventsHardCorneringEnabled,
                eventsHardCorneringErrorMessage: action.payload.eventsHardCorneringErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_HARD_CORNERING_THRESHOLD:
            return {
                ...state,
                eventsHardCorneringThresholdPending: action.payload.eventsHardCorneringThresholdPending,
                eventsHardCorneringThreshold: action.payload.eventsHardCorneringThreshold,
                eventsHardCorneringThresholdErrorMessage: action.payload.eventsHardCorneringThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_IDLING_AVAILABILITY:
            return {
                ...state,
                eventsIdlingPending: action.payload.eventsIdlingPending,
                eventsIdlingEnabled: action.payload.eventsIdlingEnabled,
                eventsIdlingErrorMessage: action.payload.eventsIdlingErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_IDLING_THRESHOLD:
            return {
                ...state,
                eventsIdlingThresholdPending: action.payload.eventsIdlingThresholdPending,
                eventsIdlingThreshold: action.payload.eventsIdlingThreshold,
                eventsIdlingThresholdErrorMessage: action.payload.eventsIdlingThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_IDLING_SPEED_THRESHOLD:
            return {
                ...state,
                eventsIdlingSpeedThresholdPending: action.payload.eventsIdlingSpeedThresholdPending,
                eventsIdlingSpeedThreshold: action.payload.eventsIdlingSpeedThreshold,
                eventsIdlingSpeedThresholdErrorMessage: action.payload.eventsIdlingSpeedThresholdErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_POSSIBLE_IMPACT_AVAILABILITY:
            return {
                ...state,
                eventsPossibleImpactPending: action.payload.eventsPossibleImpactPending,
                eventsPossibleImpactEnabled: action.payload.eventsPossibleImpactEnabled,
                eventsPossibleImpactErrorMessage: action.payload.eventsPossibleImpactErrorMessage
            }
        case ACTION_TYPES.SET_EVENTS_POSSIBLE_IMPACT_THRESHOLD:
            return {
                ...state,
                eventsPossibleImpactThresholdPending: action.payload.eventsPossibleImpactThresholdPending,
                eventsPossibleImpactThreshold: action.payload.eventsPossibleImpactThreshold,
                eventsPossibleImpactThresholdErrorMessage: action.payload.eventsPossibleImpactThresholdErrorMessage
            }


        case ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_AVAILABILITY:
            return {
                ...state,
                accountDailyFleetReportPending: action.payload.accountDailyFleetReportPending,
                accountDailyFleetReportEnabled: action.payload.accountDailyFleetReportEnabled,
                accountDailyFleetReportErrorMessage: action.payload.accountDailyFleetReportErrorMessage
            }
        case ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_TIMEZONE:
            return {
                ...state,
                accountDailyFleetReportTimezonePending: action.payload.accountDailyFleetReportTimezonePending,
                accountDailyFleetReportTimezone: action.payload.accountDailyFleetReportTimezone,
                accountDailyFleetReportTimezoneErrorMessage: action.payload.accountDailyFleetReportTimezoneErrorMessage
            }
        case ACTION_TYPES.SET_ACCOUNT_DAILYFLEETREPORT_EMAILS:
            return {
                ...state,
                accountDailyFleetReportEmailsPending: action.payload.accountDailyFleetReportEmailsPending,
                accountDailyFleetReportEmails: action.payload.accountDailyFleetReportEmails,
                accountDailyFleetReportEmailsErrorMessage: action.payload.accountDailyFleetReportEmailsErrorMessage
            }
        case ACTION_TYPES.SET_GESTURES_AVAILABILITY:
            return {
                ...state,
                gesturesPending: action.payload.gesturesPending,
                gesturesEnabled: action.payload.gesturesEnabled,
                gesturesErrorMessage: action.payload.gesturesErrorMessage
            }
        case ACTION_TYPES.SET_INTEGRATIONS_FLEETIO_CREDENTIALS:
            return {
                ...state,
                integrationsFleetioCredentialsPending: action.payload.integrationsFleetioCredentialsPending,
                integrationsFleetioCredentialsAccountToken: action.payload.integrationsFleetioCredentialsAccountToken,
                integrationsFleetioCredentialsApiKey: action.payload.integrationsFleetioCredentialsApiKey,
                integrationsFleetioCredentialsIntegrationId: action.payload.integrationsFleetioCredentialsIntegrationId,
                integrationsFleetioCredentialsStatus: action.payload.integrationsFleetioCredentialsStatus,
                integrationsFleetioCredentialsStatusMessage: action.payload.integrationsFleetioCredentialsStatusMessage,
                integrationsFleetioCredentialsErrorMessage: action.payload.integrationsFleetioCredentialsErrorMessage
            }
        case ACTION_TYPES.SET_INTEGRATIONS_FLEETIO_CREDENTIALS_VERIFICATION:
            return {
                ...state,
                integrationsFleetioCredentialsVerificationPending: action.payload.integrationsFleetioCredentialsVerificationPending,
                integrationsFleetioCredentialsVerificationIsValid: action.payload.integrationsFleetioCredentialsVerificationIsValid,
                integrationsFleetioCredentialsVerificationErrorMessage: action.payload.integrationsFleetioCredentialsVerificationErrorMessage
            }
        case ACTION_TYPES.SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS:
            return {
                ...state,
                integrationsWhiparoundCredentialsPending: action.payload.integrationsWhiparoundCredentialsPending,
                integrationsWhiparoundCredentialsApiKey: action.payload.integrationsWhiparoundCredentialsApiKey,
                integrationsWhiparoundCredentialsIntegrationId: action.payload.integrationsWhiparoundCredentialsIntegrationId,
                integrationsWhiparoundCredentialsStatus: action.payload.integrationsWhiparoundCredentialsStatus,
                integrationsWhiparoundCredentialsStatusMessage: action.payload.integrationsWhiparoundCredentialsStatusMessage,
                integrationsWhiparoundCredentialsErrorMessage: action.payload.integrationsWhiparoundCredentialsErrorMessage
            }
        case ACTION_TYPES.SET_INTEGRATIONS_WHIPAROUND_CREDENTIALS_VERIFICATION:
            return {
                ...state,
                integrationsWhiparoundCredentialsVerificationPending: action.payload.integrationsWhiparoundCredentialsVerificationPending,
                integrationsWhiparoundCredentialsVerificationIsValid: action.payload.integrationsWhiparoundCredentialsVerificationIsValid,
                integrationsWhiparoundCredentialsVerificationErrorMessage: action.payload.integrationsWhiparoundCredentialsVerificationErrorMessage
            }
        case ACTION_TYPES.SET_UNIT_OF_MEASURE_DISTANCE:
            return {
                ...state,
                unitOfMeasureDistancePending: action.payload.unitOfMeasureDistancePending,
                unitOfMeasureDistanceMetric: action.payload.unitOfMeasureDistanceMetric,
                unitOfMeasureDistanceErrorMessage: action.payload.unitOfMeasureDistanceErrorMessage
            }
        case ACTION_TYPES.SET_UNIT_OF_MEASURE_TEMPERATURE:
            return {
                ...state,
                unitOfMeasureTemperaturePending: action.payload.unitOfMeasureTemperaturePending,
                unitOfMeasureTemperatureMetric: action.payload.unitOfMeasureTemperatureMetric,
                unitOfMeasureTemperatureErrorMessage: action.payload.unitOfMeasureTemperatureErrorMessage
            }
        case ACTION_TYPES.SET_WIFI_HOTSPOT_AVAILABILITY:
            return {
                ...state,
                wifiHotspotsPending: action.payload.wifiHotspotsPending,
                wifiHotspotsEnabled: action.payload.wifiHotspotsEnabled,
                wifiHotspotsErrorMessage: action.payload.wifiHotspotsErrorMessage
            }
        case ACTION_TYPES.SET_WIFI_HOTSPOT_CREDENTIALS:
            return {
                ...state,
                wifiHotspotsCredentialsPending: action.payload.wifiHotspotsCredentialsPending,
                wifiHotspotsCredentialsSsid: action.payload.wifiHotspotsCredentialsSsid,
                wifiHotspotsCredentialsPassword: action.payload.wifiHotspotsCredentialsPassword,
                wifiHotspotsCredentialsErrorMessage: action.payload.wifiHotspotsCredentialsErrorMessage
            }
        case ACTION_TYPES.SET_FACIAL_RECOGNITION_AVAILABILITY:
            return {
                ...state,
                facialRecognitionPending: action.payload.facialRecognitionPending,
                facialRecognitionEnabled: action.payload.facialRecognitionEnabled,
                facialRecognitionErrorMessage: action.payload.facialRecognitionErrorMessage
            }
        default:
            return state; // initial state
    }
}

export class GlobalSettingsAPI {

    // Retrieve a valid API JWT; if missing/expired (local getter returns null) perform a refresh.
    // Centralized so feature pages left open beyond JWT_EXPIRATION_DURATION( e.g. 2 mins) still work.
    static async getOrRefreshApiJWT(stage){
        let apiJWT = getLocalAPIJWT();
        if (apiJWT) return apiJWT;
        return GlobalSettingsAPI.refreshTempCredentials(stage);
    }

    static refreshTempCredentials (stage) {

        if (!stage) return Promise.reject(new Error('stage is required'));

        const userPoolIdTokenJWT = CognitoUtil.getIdTokenJWT();
        const userAccount = CognitoUtil.getUserAccountsDetails();

        return getAPIJWT(userPoolIdTokenJWT, userAccount, stage)
        .then(
            (apiJWT) => {
                setLocalAPIJWT(apiJWT);
                return Promise.resolve(apiJWT);
            },
            (error) => {return Promise.reject(error);} // passed to .catch below
        )
        .catch( (error) => {
            if (error.code) {
                switch (error.code) {
                    case 401: // Unauthorized due to expired temporary user session, try to refresh
                        return new Promise( (resolve, reject) => {
                            CognitoUtil.refreshLogin(
                                () => {
                                    const discardedPromise = GlobalSettingsAPI.refreshTempCredentials(stage)
                                    // IMPORTANT: above, not returning GlobalSettingsAPI.refreshTempCredentials promise. Instead, calling "resolve" or "reject" functions from parent promise wrapper b/c CognitoUtil.refreshLogin does not return a promise
                                    .then(
                                        (newApiJWT) => {
                                            resolve(newApiJWT);
                                            return;
                                        },
                                        (error) => {return Promise.reject(error);}
                                    )
                                    .catch( (error) => {
                                        reject(error);
                                        return;
                                    });
                                },
                                () => {
                                    reject();
                                    return;
                                }
                            );
                        });
                    default:
                        console.error("GlobalSettingsAPI refreshTempCredentials error", error);
                }
            }
            return Promise.reject(error); // passed to .catch below
        });
    }

    // Generic PATCH helper to DRY repeated mutation logic.
    // Performs: stage validation, apiJWT retrieval, PATCH request, single 401 refresh + retry guard.
    static _patchSettings(stage, delta, retryInvoker, attempt = 1) {
        if (!stage) return Promise.reject(new Error('stage is required'));
        return GlobalSettingsAPI.getOrRefreshApiJWT(stage).then(apiJWT => {
            const apiBaseUrl = getApiBaseUrl('/user-v1');
            const method = 'PATCH';
            return apiRequest(`${apiBaseUrl}/account/settings`, apiJWT, method, delta)
                .then(() => Promise.resolve(), e => Promise.reject(e))
                .catch(error => {
                    if (error.code === 401) {
                        if (attempt >= 2) { // guard: only one refresh attempt
                            console.error("GlobalSettingsAPI _patchSettings aborting after repeated 401", error);
                            return Promise.reject(error);
                        }
                        return GlobalSettingsAPI.refreshTempCredentials(stage)
                            .then(() => retryInvoker(attempt + 1))
                            .catch(err => { console.error("GlobalSettingsAPI _patchSettings refreshTempCredentials error", err); return Promise.reject(err); });
                    }
                    return Promise.reject(error);
                });
        });
    }

    static loadIntegrationSettingsOnly (stage) { // a special request for just integrations is required on a success response for adding new credentials

        if (!stage) return Promise.reject(new Error('stage is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        return RcAPI.apiRequest(`${apiBaseUrl}/integrations`, "GET", {}, stage) // kind of as if it's /account/integrations
        .then(
            (accountIntegrations) => {
                let integrations = {
                    fleetio: {}, // default, empty object is treated as not configured yet
                    whiparound: {}
                }
                // Fleetio and Whiparound are the only integrations we're interested in at this time
                if (!accountIntegrations || !accountIntegrations.results || !accountIntegrations.results.length) {
                    if (!accountIntegrations) {console.warn("GlobalSettingsAPI getAccountSettings integrations API reponse is empty. Assuming no integrations.");}
                    return Promise.resolve(integrations); // return unset integration (assuming this just means that the account doesn't have any integrations set up yet)
                }

                for (let i = 0; i < accountIntegrations.results.length; i += 1) {
                    if (accountIntegrations.results[i]) {
                        const integrationDetailsJSON = accountIntegrations.results[i].details;
                        const integrationId = accountIntegrations.results[i].integrationId;
                        const stat = accountIntegrations.results[i]['status'];
                        const statusMessage = accountIntegrations.results[i]['status_message'];
                        let integrationDetails = {};
                        if (integrationDetailsJSON) {
                            if (typeof integrationDetailsJSON === "string") {
                                integrationDetails = JSON.parse(integrationDetailsJSON);
                            } else {
                                integrationDetails = integrationDetailsJSON;
                            }
                            integrationDetails.integrationId = integrationId;
                            integrationDetails['status'] = stat;
                            integrationDetails['statusMessage'] = statusMessage;
                        }

                        integrations[accountIntegrations.results[i].integrationType.toLowerCase()] = integrationDetails; // {account_token: "6...d57",endpoint: "https://secure.fleetio.com/api/v1/",token
                    }
                }
                return Promise.resolve(integrations);
            }
        )
        .catch((error) => {
            return Promise.reject(error);
        });
    }

    static getAccountSettings (stage, dispatch = undefined) {

        function setAccountSettingsStore (accountSettingsOdject) {

            if (!accountSettingsOdject) return Promise.reject(new Error('accountSettingsOdject not found'));
            if (!accountSettingsOdject.hasOwnProperty('raven')) return Promise.reject(new Error('accountSettingsOdject invalid, raven property missing'));
            if (!accountSettingsOdject.hasOwnProperty('account')) return Promise.reject(new Error('accountSettingsOdject invalid, account property missing'));

            const globalAccountDailyFleetReportSettings = accountSettingsOdject.account.dailyFleetReport ? accountSettingsOdject.account.dailyFleetReport : {};
            if (globalAccountDailyFleetReportSettings.hasOwnProperty("enabled")) {
                dispatch(ACTIONS.setAccountDailyFleetReportAvailability(globalAccountDailyFleetReportSettings.enabled));
            } else {
                dispatch(ACTIONS.setAccountDailyFleetReportAvailability(null));
                //dispatch(ACTIONS.setAccountDailyFleetReportAvailability(undefined, strings.settings_error_setting_not_found));
            }

            if (globalAccountDailyFleetReportSettings.hasOwnProperty("timezone")) {
                dispatch(ACTIONS.setAccountDailyFleetReportTimezone(globalAccountDailyFleetReportSettings.timezone));
            } else {
                dispatch(ACTIONS.setAccountDailyFleetReportTimezone(null));
            }

            if (globalAccountDailyFleetReportSettings.hasOwnProperty("emails")) {
                dispatch(ACTIONS.setAccountDailyFleetReportEmails(globalAccountDailyFleetReportSettings.emails));
            } else {
                dispatch(ACTIONS.setAccountDailyFleetReportEmails(null));
            }


            const globalAccountIntegrationsSettings = accountSettingsOdject.account.integrations ? accountSettingsOdject.account.integrations : {};

            if (globalAccountIntegrationsSettings.hasOwnProperty("fleetio")) {
                const accountToken = globalAccountIntegrationsSettings.fleetio.account_token;
                const apiKey = globalAccountIntegrationsSettings.fleetio.token;
                const integrationId = globalAccountIntegrationsSettings.fleetio.integrationId;
                const stat = globalAccountIntegrationsSettings.fleetio['status']
                const statusMessage = globalAccountIntegrationsSettings.fleetio.statusMessage;
                if (accountToken && apiKey && integrationId) {
                    dispatch(ACTIONS.setIntegrationsFleetioCredentials(accountToken, apiKey, integrationId, stat, statusMessage));
                } else {
                    dispatch(ACTIONS.setIntegrationsFleetioCredentials(undefined, undefined, undefined, undefined, undefined, undefined)); // leave empty, clear pending state
                }
            } else {
                //dispatch(ACTIONS.setIntegrationsFleetioCredentials(undefined, undefined)); // leave empty, clear pending state
                dispatch(ACTIONS.setIntegrationsFleetioCredentials(undefined, undefined, undefined, undefined, undefined, strings.settings_error_setting_not_found));
            }

            if (globalAccountIntegrationsSettings.hasOwnProperty("whiparound")) {
                const apiKey = globalAccountIntegrationsSettings.whiparound.api_key;
                const integrationId = globalAccountIntegrationsSettings.whiparound.integrationId;
                const stat = globalAccountIntegrationsSettings.whiparound['status']
                const statusMessage = globalAccountIntegrationsSettings.whiparound.statusMessage;
                if (apiKey && integrationId) {
                    dispatch(ACTIONS.setIntegrationsWhiparoundCredentials(apiKey, integrationId, stat, statusMessage));
                } else {
                    dispatch(ACTIONS.setIntegrationsWhiparoundCredentials(undefined, undefined, undefined, undefined, undefined)); // leave empty, clear pending state
                }
            } else {
                //dispatch(ACTIONS.setIntegrationsWhiparoundCredentials(undefined, undefined)); // leave empty, clear pending state
                dispatch(ACTIONS.setIntegrationsWhiparoundCredentials(undefined, undefined, undefined, undefined, strings.settings_error_setting_not_found));
            }

            // hint: dispatch(ACTIONS.setIntegrationsFleetioCredentialsVerification(isValid)); // Roughed in feature to be completed when "test" endpoint is available

            const globalAccountFacialRecognitionSettings = accountSettingsOdject.account.facialRecognition ? accountSettingsOdject.account.facialRecognition : {};
            if (globalAccountFacialRecognitionSettings.hasOwnProperty("enabled")) {
                dispatch(ACTIONS.setFacialRecognitionAvailability(globalAccountFacialRecognitionSettings.enabled));
            } else {
                dispatch(ACTIONS.setFacialRecognitionAvailability(null));
            }

            const globalRavensAudioSettings = accountSettingsOdject.raven.audio ? accountSettingsOdject.raven.audio : {};
            if (globalRavensAudioSettings.hasOwnProperty("audio_notifications_enabled")) {
                dispatch(ACTIONS.setAudioNotificationsAvailability(globalRavensAudioSettings.audio_notifications_enabled));
            } else {
                dispatch(ACTIONS.setAudioNotificationsAvailability(undefined, strings.settings_error_setting_not_found));
            }


            const globalRavensDisplaySettings = accountSettingsOdject.raven.display ? accountSettingsOdject.raven.display : {};
            if (globalRavensDisplaySettings && globalRavensDisplaySettings.distance_unit) { // "IMPERIAL"/"METRIC"
                const unitOfMeasureDistanceMetric = globalRavensDisplaySettings.distance_unit.toLowerCase() === "metric" ? true : false;
                dispatch(ACTIONS.setUnitOfMeasureDistance(unitOfMeasureDistanceMetric));
            } else {
                dispatch(ACTIONS.setUnitOfMeasureDistance(undefined, strings.settings_error_setting_not_found));
            }

            if (globalRavensDisplaySettings && globalRavensDisplaySettings.temperature_unit) { // "F"/"C"
                const unitOfMeasureTemperatureMetric = globalRavensDisplaySettings.temperature_unit.toLowerCase() === "c" ? true : false;
                dispatch(ACTIONS.setUnitOfMeasureTemperature(unitOfMeasureTemperatureMetric));
            } else {
                dispatch(ACTIONS.setUnitOfMeasureTemperature(undefined, strings.settings_error_setting_not_found));
            }


            // NEW (2025) events_config format supersedes legacy flat raven.events.* keys. Prefer events_config when present.
            const globalRavensEventsConfigSettings = accountSettingsOdject.raven.events_config ? accountSettingsOdject.raven.events_config : {};

            const getConfig = (eventConfigKey) => {
                return globalRavensEventsConfigSettings[eventConfigKey];
            };
            // Speeding (new: events_config.speeding.*)
            const speedingCfg = getConfig('speeding');
            if (speedingCfg && speedingCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsSpeedingAvailability(speedingCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsSpeedingAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const eventsSpeedingThreshold = speedingCfg ? speedingCfg.threshold : undefined;
            const eventsSpeedingThresholdType = speedingCfg ? speedingCfg.threshold_type : undefined;
            if (eventsSpeedingThreshold !== undefined && eventsSpeedingThresholdType) {
                dispatch(ACTIONS.setEventsSpeedingThreshold(eventsSpeedingThreshold, eventsSpeedingThresholdType));
            } else {
                dispatch(ACTIONS.setEventsSpeedingThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }

            const accelCfg = getConfig('aggressive_accel');
            if (accelCfg && accelCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsAggressiveAccelAvailability(accelCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsAggressiveAccelAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const eventsAggressiveAccelThreshold = accelCfg ? accelCfg.accel_threshold : undefined;
            if (eventsAggressiveAccelThreshold !== undefined) {
                dispatch(ACTIONS.setEventsAggressiveAccelThreshold(eventsAggressiveAccelThreshold));
            } else {
                dispatch(ACTIONS.setEventsAggressiveAccelThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }

            const tgCfg = getConfig('tailgating_detection');
            if (tgCfg && tgCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsTailgatingDetectionAvailability(tgCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsTailgatingDetectionAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const speed = tgCfg ? tgCfg.speed_threshold : undefined;
            if (speed !== undefined) {
                dispatch(ACTIONS.setEventsTailgatingDetectionSpeedThreshold(speed));
            } else {
                dispatch(ACTIONS.setEventsTailgatingDetectionSpeedThreshold(undefined, strings.settings_error_setting_not_found));
            }
            const reaction = tgCfg ? tgCfg.alert_reaction_time : undefined;
            if (reaction !== undefined) {
                dispatch(ACTIONS.setEventsTailgatingDetectionReactionTimeThreshold(reaction));
            } else {
                dispatch(ACTIONS.setEventsTailgatingDetectionReactionTimeThreshold(undefined, strings.settings_error_setting_not_found));
            }
            const followTime = tgCfg ? tgCfg.follow_time : undefined;
            if (followTime !== undefined) {
                dispatch(ACTIONS.setEventsTailgatingDetectionFollowTimeThreshold(followTime));
            } else {
                dispatch(ACTIONS.setEventsTailgatingDetectionFollowTimeThreshold(undefined, strings.settings_error_setting_not_found));
            }

            // Car Bumped
            const cbCfg = getConfig('car_bumped');
            if (cbCfg && cbCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsCarBumpedAvailability(cbCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsCarBumpedAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const cbThresh = cbCfg ? cbCfg.accel_threshold : undefined;
            if (cbThresh !== undefined) {
                dispatch(ACTIONS.setEventsCarBumpedThreshold(cbThresh));
            } else {
                dispatch(ACTIONS.setEventsCarBumpedThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }


            // Generic availability for all other events (use new config first)
            for (let event of events) {
                let enabled;
                const cfg = getConfig(event.snakeName);
                if (cfg && cfg.hasOwnProperty('enabled')) {
                    enabled = cfg.enabled;
                } else {
                    // Silence warning for events that might not yet be exposed in events_config
                    console.warn("GlobalSettingsAPI events_config missing enabled for", event.snakeName);
                }
                if (enabled !== undefined) {
                    dispatch(ACTIONS["setEvents" + event.pascalName + "Availability"](enabled));
                } else {
                    dispatch(ACTIONS["setEvents" + event.pascalName + "Availability"](undefined, strings.settings_error_setting_not_found));
                }
            }

            // Audio alert type for all events (hard-coded + generic)
            for (let event of ALL_EVENT_NAMES) {
                const cfg = getConfig(event.snakeName);
                if (cfg && cfg.hasOwnProperty('audio_alert')) {
                    dispatch(ACTIONS["setEvents" + event.pascalName + "AudioAlert"](cfg.audio_alert));
                } else {
                    // undefined signals this event does not support audio_alert
                    dispatch(ACTIONS["setEvents" + event.pascalName + "AudioAlert"](undefined));
                }
            }

            // Visual alert enabled for all events (hard-coded + generic)
            for (let event of ALL_EVENT_NAMES) {
                const cfg = getConfig(event.snakeName);
                // Speeding: ignore visual_alert_enabled from API (cloud/firmware bug includes it but device does not support it) (RAV-8176)
                if (event.snakeName === 'speeding') {
                    dispatch(ACTIONS["setEvents" + event.pascalName + "VisualAlert"](undefined));
                } else if (cfg && cfg.hasOwnProperty('visual_alert_enabled')) {
                    dispatch(ACTIONS["setEvents" + event.pascalName + "VisualAlert"](cfg.visual_alert_enabled));
                } else {
                    // undefined signals this event does not support visual_alert_enabled
                    dispatch(ACTIONS["setEvents" + event.pascalName + "VisualAlert"](undefined));
                }
            }

            // Hard Braking
            const hbCfg = getConfig('hard_braking'); // mapped to hard_braking
            if (hbCfg && hbCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsHardBrakingAvailability(hbCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsHardBrakingAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const hbThresh = hbCfg ? hbCfg.accel_threshold : undefined;
            if (hbThresh !== undefined) {
                dispatch(ACTIONS.setEventsHardBrakingThreshold(hbThresh));
            } else {
                dispatch(ACTIONS.setEventsHardBrakingThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }

            // Harsh/Hard Cornering
            const hcCfg = getConfig('hard_cornering'); // maps to hard_cornering
            if (hcCfg && hcCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsHardCorneringAvailability(hcCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsHardCorneringAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const hcThresh = hcCfg ? hcCfg.accel_threshold : undefined;
            if (hcThresh !== undefined) {
                dispatch(ACTIONS.setEventsHardCorneringThreshold(hcThresh));
            } else {
                dispatch(ACTIONS.setEventsHardCorneringThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }

            const idlingCfg = getConfig('idling');
            if (idlingCfg && idlingCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsIdlingAvailability(idlingCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsIdlingAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const idlingGrace = idlingCfg ? idlingCfg.grace_period : undefined;
            if (idlingGrace !== undefined) {
                dispatch(ACTIONS.setEventsIdlingThreshold(idlingGrace));
            } else {
                dispatch(ACTIONS.setEventsIdlingThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }
            const idlingSpeedFloor = idlingCfg ? idlingCfg.speed_floor : undefined;
            if (idlingSpeedFloor !== undefined) {
                dispatch(ACTIONS.setEventsIdlingSpeedThreshold(idlingSpeedFloor));
            } else {
                dispatch(ACTIONS.setEventsIdlingSpeedThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }

            const impactCfg = getConfig('possible_impact');
            if (impactCfg && impactCfg.hasOwnProperty('enabled')) {
                dispatch(ACTIONS.setEventsPossibleImpactAvailability(impactCfg.enabled));
            } else {
                dispatch(ACTIONS.setEventsPossibleImpactAvailability(undefined, strings.settings_error_setting_not_found));
            }
            const impactThresh = impactCfg ? impactCfg.accel_threshold : undefined;
            if (impactThresh !== undefined) {
                dispatch(ACTIONS.setEventsPossibleImpactThreshold(impactThresh));
            } else {
                dispatch(ACTIONS.setEventsPossibleImpactThreshold(undefined, undefined, strings.settings_error_setting_not_found));
            }

            const globalRavensSystemSettings = accountSettingsOdject.raven.system ? accountSettingsOdject.raven.system : {};
            if (globalRavensSystemSettings.hasOwnProperty("gesture_enabled")) {
                dispatch(ACTIONS.setGesturesAvailability(globalRavensSystemSettings.gesture_enabled));
            } else {
                console.log("GlobalSettingsAPI setAccountSettingsStore.raven.system.gesture_enabled NOT 1592");
                dispatch(ACTIONS.setGesturesAvailability(undefined, strings.settings_error_setting_not_found));
            }

            const globalRavensWiFiHotspotSettings = accountSettingsOdject.raven.wifi_hotspot ? accountSettingsOdject.raven.wifi_hotspot : {};
            if (globalRavensWiFiHotspotSettings.hasOwnProperty("hotspot_enabled")) {
                dispatch(ACTIONS.setWiFiHotspotAvailability(globalRavensWiFiHotspotSettings.hotspot_enabled));
            } else {
                dispatch(ACTIONS.setWiFiHotspotAvailability(undefined, strings.settings_error_setting_not_found));
            }
            let wifiHotspotsCredentialsSsid = undefined;
            let wifiHotspotsCredentialsPassword = undefined;
            if (globalRavensWiFiHotspotSettings.hasOwnProperty("ssid")) {
                wifiHotspotsCredentialsSsid = globalRavensWiFiHotspotSettings.ssid;
            } else {
                console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.wifi_hotspot missing ssid property");
            }
            if (globalRavensWiFiHotspotSettings.hasOwnProperty("password")) {
                wifiHotspotsCredentialsPassword = globalRavensWiFiHotspotSettings.password;
            } else {
                console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.wifi_hotspot missing password property");
            }
            if (wifiHotspotsCredentialsSsid && wifiHotspotsCredentialsPassword) {
                dispatch(ACTIONS.setWiFiHotspotCredentials(wifiHotspotsCredentialsSsid, wifiHotspotsCredentialsPassword));
            } else {
                // if not found, assume the global SSID and password credentials have not been set yet
                //dispatch(ACTIONS.setWiFiHotspotCredentials(undefined, undefined, strings.settings_error_setting_not_found));
                dispatch(ACTIONS.setWiFiHotspotCredentials(undefined, undefined)); // leave empty, clear pending state
            }




            const globalRavensCameraSettings = accountSettingsOdject.raven.camera ? accountSettingsOdject.raven.camera : {};
            let cameraAudioEnabled = undefined;
            let cameraRoadEnabled = undefined;
            let cameraCabinEnabled = undefined;
            const videoRecordingProfile = globalRavensCameraSettings.video_recording_profile;

            if (globalRavensCameraSettings.road_camera) {

                if (globalRavensCameraSettings.road_camera.hasOwnProperty("camera_enabled")) {
                    cameraRoadEnabled = globalRavensCameraSettings.road_camera.camera_enabled;
                } else {
                    console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.camera.road_camera missing camera_enabled property");
                    cameraRoadEnabled = undefined;
                }

                if (globalRavensCameraSettings.road_camera.hasOwnProperty("audio_recording")) {
                    cameraAudioEnabled = globalRavensCameraSettings.road_camera.audio_recording;
                } else {
                    console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.camera.road_camera missing audio_recording property");
                    cameraAudioEnabled = undefined;
                }

            } else {
                console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.camera missing road_camera property");
                cameraRoadEnabled = undefined;
                cameraAudioEnabled = undefined;
            }

            if (globalRavensCameraSettings.cabin_camera) {

                if (globalRavensCameraSettings.cabin_camera.hasOwnProperty("camera_enabled")) {
                    cameraCabinEnabled = globalRavensCameraSettings.cabin_camera.camera_enabled;
                } else {
                    console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.camera.cabin_camera missing camera_enabled property");
                    cameraCabinEnabled = undefined;
                }

                if (cameraAudioEnabled !== undefined) { // IMPORTANT: road_camera checked first (∴ value of undefined takes precedence)
                    if (globalRavensCameraSettings.cabin_camera.hasOwnProperty("audio_recording")) {
                        cameraAudioEnabled = globalRavensCameraSettings.cabin_camera.audio_recording;
                    } else {
                        console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.camera.cabin_camera missing audio_recording property");
                        cameraAudioEnabled = undefined;
                    }    
                }
            } else {
                console.warn("GlobalSettingsAPI setAccountSettingsStore.raven.camera missing cabin_camera property");
                cameraCabinEnabled = undefined;
                cameraAudioEnabled = undefined;
            }

            if (cameraAudioEnabled !== undefined) {
                dispatch(ACTIONS.setAudioAvailability(cameraAudioEnabled));
            } else {
                dispatch(ACTIONS.setAudioAvailability(undefined, strings.settings_error_setting_not_found));
            }

            /* This has been moved to a conditional on render() in the GlobalSettings component (instant feedback during updates)
            if (!cameraRoadEnabled && !cameraCabinEnabled) { // false or undefined
                dispatch(ACTIONS.setAudioAvailability(cameraAudioEnabled, strings.settings_enable_cameras_for_audio));
            }*/

            if (cameraRoadEnabled !== undefined) {
                dispatch(ACTIONS.setCameraRoadAvailability(cameraRoadEnabled));
            } else {
                dispatch(ACTIONS.setCameraRoadAvailability(undefined, strings.settings_error_setting_not_found));
            }

            if (cameraCabinEnabled !== undefined) {
                dispatch(ACTIONS.setCameraCabinAvailability(cameraCabinEnabled));
            } else {
                dispatch(ACTIONS.setCameraCabinAvailability(undefined, strings.settings_error_setting_not_found));
            }

            if (videoRecordingProfile) {
                dispatch(ACTIONS.setVideoRecordingProfile(videoRecordingProfile));
            } else {
                dispatch(ACTIONS.setVideoRecordingProfile(undefined, strings.settings_error_setting_not_found));
            }

            const audioSettingsOdject = accountSettingsOdject.raven.audio ? accountSettingsOdject.raven.audio : {};
            if (audioSettingsOdject.hasOwnProperty("streaming_audio_enabled")) {
                dispatch(ACTIONS.setStreamingVideoAudioAvailability(audioSettingsOdject.streaming_audio_enabled));
            } else {
                dispatch(ACTIONS.setStreamingVideoAudioAvailability(undefined, strings.settings_error_setting_not_found));
            }
        }
        if (!stage) return Promise.reject(new Error('stage is required'));
        const apiBaseUrl = getApiBaseUrl('/user-v1');

        const data = {};
        const method = 'GET';
        return Promise.all([

            new Promise((resolve, reject) => {
                // Global Settings, short and sweet for ~90% of the settings
                return RcAPI.apiRequest(`${apiBaseUrl}/account/settings`, method, data, stage)
                .then((response) => {
                    /* sample response

                    {
                        "account": {...},
                        "raven": {
                            "display": {...},
                            "system": {
                                "asset_ble_tracking_enabled": false,
                                "bluetooth_button_enabled": true,
                                "gesture_enabled": false,
                                "video_recording_after_parked_duration": 30,
                                "vehicle_speed_adjustment_percent": 100
                            },
                            "camera": {
                                "road_camera": {
                                    "camera_enabled": true,
                                    "video_enabled": true,
                                    "timelapse_enabled": true,
                                    "audio_recording": true
                                },
                                "cabin_camera": {
                                    "camera_enabled": true,
                                    "video_enabled": true,
                                    "timelapse_enabled": true,
                                    "audio_recording": true
                                },
                                "video_recording_profile": "standard"
                            },
                            "audio": {
                                "audio_notifications_enabled": true,
                                "streaming_audio_enabled": true,
                                "message_notification_audio_enabled": true
                            },
                            "events": {..},
                            "events_config": {
                                "aggressive_accel": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "NONE",
                                    "max_events_per_day": 50,
                                    "accel_threshold": 320
                                },
                                "bad_install": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "NONE",
                                    "visual_alert_enabled": false,
                                    "visual_alert_frequency_mins": -1
                                },
                                "camera_obscured": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 10,
                                    "grace_period": 60000
                                },
                                "car_bumped": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "max_events_per_day": 50,
                                    "accel_threshold": 46
                                },
                                "cellphone_detection": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 10,
                                    "grace_period": 60000
                                },
                                "distracted_detection": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 10,
                                    "grace_period": 60000
                                },
                                "drinking_detection": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 10,
                                    "grace_period": 60000
                                },
                                "eating_detection": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 10,
                                    "grace_period": 60000
                                },
                                "hard_braking": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": true,
                                    "audio_alert": "NONE",
                                    "max_events_per_day": 50,
                                    "accel_threshold": -400
                                },
                                "hard_cornering": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "NONE",
                                    "max_events_per_day": 50,
                                    "accel_threshold": 480
                                },
                                "idling": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "grace_period": 300000,
                                    "speed_floor": 2
                                },
                                "possible_impact": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": true,
                                    "audio_alert": "NONE",
                                    "max_events_per_day": 50,
                                    "accel_threshold": 2000
                                },
                                "potential_red_light_camera_violation": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": false
                                },
                                "potential_speed_camera_violation": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": false,
                                    "speed_threshold": 10
                                },
                                "refueling": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false
                                },
                                "smoking_detection": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 0,
                                    "grace_period": 60000
                                },
                                "speeding": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": false,
                                    "threshold": 10,
                                    "threshold_type": "CONSTANT",
                                    "visual_warning_threshold": 0,
                                    "visual_warning_threshold_type": "CONSTANT"
                                },
                                "stop_sign_violation": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": false,
                                    "speed_threshold": 7
                                },
                                "tailgating_detection": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 50,
                                    "grace_period": 15,
                                    "follow_time": 5,
                                    "critical_reaction_time": 0.2,
                                    "alert_reaction_time": 1,
                                    "safe_reaction_time": 2,
                                    "vanishing_point_calibration_enabled": true,
                                    "vanishing_point_calibration_force": false
                                },
                                "tired_detection": {
                                    "enabled": true,
                                    "auto_video_upload_enabled": false,
                                    "audio_alert": "BEEP",
                                    "visual_alert_enabled": true,
                                    "speed_threshold": 10,
                                    "grace_period": 60000
                                }
                            },
                            "obd": {
                                "canbus_enabled": true,
                                "low_battery_cutoff_millivolts": 12000,
                                "non_can_off_delay": 0
                            },
                            "eld": {
                                "eld_enabled": false,
                                "eld_visual_ud_alert_enabled": true,
                                "eld_visual_ud_alert_period": 30000
                            },
                            "wifi_hotspot": {
                                "hotspot_enabled": false,
                                "auto_disable_on_engine_off": true,
                                "ssid": null,
                                "password": null
                            },
                            "driver_id": {
                                "barcode_driver_id_enabled": false,
                                "barcode_driver_id_request_period": "PER_TRIP",
                                "barcode_driver_id_audio_delay": -1,
                                "barcode_driver_id_check_at_idle": 0,
                                "barcode_driver_id_grace_period": 0
                            }
                        }
                    }

                    */
                    return resolve(response)
                })
                .catch((error) => reject(error));
            }),

            new Promise((resolve, reject) => {
                return GlobalSettingsAPI.loadIntegrationSettingsOnly(stage) // kind of as if it's /account/integrations
                .then(
                    (integrations) => {
                        resolve(integrations);
                    }
                )
                .catch((error) => {
                    //reject(error);
                    console.error("GlobalSettingsAPI getAccountSettings loadIntegrationSettingsOnly error", error);
                    resolve(undefined); // return no inegrations such that error is shown in UI
                });
            })
        ])
        .then(
            (responses) => {
                let mergedResponse = {...responses[0]};
                if (!mergedResponse.account) {
                    mergedResponse.account = {};
                }
                mergedResponse.account.integrations = responses[1];

                if (dispatch) {
                    setAccountSettingsStore(mergedResponse);
                } else {
                    console.warn("GlobalSettingsAPI getAccountSettings skipping updating redux store");
                }
                return Promise.resolve(mergedResponse);
            }
        )
        .catch((error) => { // catches other exceptions and promise rejects above            
            console.error("GlobalSettingsAPI getAccountSettings error", error);
            return Promise.reject(error);
        });
    }


    static setAudio (isEnabled, stage, __attempt = 1) {
        const delta = { raven: { camera: { road_camera: { audio_recording: isEnabled }, cabin_camera: { audio_recording: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setAudio(isEnabled, stage, nextAttempt), __attempt);
    }

    static setStreamingVideoAudio (isEnabled, stage, __attempt = 1) {
        const delta = { raven: { audio: { streaming_audio_enabled: isEnabled } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setStreamingVideoAudio(isEnabled, stage, nextAttempt), __attempt);
    }

    static setAudioNotifications (isEnabled, stage, __attempt = 1) {
        const delta = { raven: { audio: { audio_notifications_enabled: isEnabled } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setAudioNotifications(isEnabled, stage, nextAttempt), __attempt);
    }

    static setCameraCabin (isEnabled, stage, __attempt = 1) {
        const delta = { raven: { camera: { cabin_camera: { camera_enabled: isEnabled, video_enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setCameraCabin(isEnabled, stage, nextAttempt), __attempt);
    }

    static setCameraRoad (isEnabled, stage, __attempt = 1) {
        const delta = { raven: { camera: { road_camera: { camera_enabled: isEnabled, video_enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setCameraRoad(isEnabled, stage, nextAttempt), __attempt);
    }

    static setVideoRecordingProfile (videoRecordingProfile, stage, __attempt = 1) {
        const delta = { raven: { camera: { video_recording_profile: videoRecordingProfile } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setVideoRecordingProfile(videoRecordingProfile, stage, nextAttempt), __attempt);
    }

    static setEventsSpeeding (isEnabled, stage, __attempt = 1) {
        const delta = { raven: { events_config: { speeding: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setEventsSpeeding(isEnabled, stage, nextAttempt), __attempt);
    }

    static setEventsSpeedingThreshold (threshold, thresholdType, stage) {
        const delta = { raven: { events_config: { speeding: { threshold: threshold, threshold_type: thresholdType } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (next) => GlobalSettingsAPI.setEventsSpeedingThreshold(threshold, thresholdType, stage), 1);
    }

    static setEventsAggressiveAccel (isEnabled, stage, __attempt = 1) {
        const delta = { raven: { events_config: { aggressive_accel: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setEventsAggressiveAccel(isEnabled, stage, nextAttempt), __attempt);
    }

    static setEventsAggressiveAccelThreshold (threshold, stage) {
        const delta = { raven: { events_config: { aggressive_accel: { accel_threshold: parseInt(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsAggressiveAccelThreshold(threshold, stage), 1);
    }

    static setEventsTailgatingDetection (isEnabled, stage, __attempt = 1) {
        console.log("GlobalSettingsAPI setEventsTailgatingDetection", isEnabled, stage);
        const delta = { raven: { events_config: { tailgating_detection: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, (nextAttempt) => GlobalSettingsAPI.setEventsTailgatingDetection(isEnabled, stage, nextAttempt), __attempt);
    }

    static setEventsTailgatingDetectionSpeedThreshold (threshold, stage) {
        console.log("GlobalSettingsAPI setEventsTailgatingDetectionSpeedThreshold", threshold, stage);
        const delta = { raven: { events_config: { tailgating_detection: { speed_threshold: parseInt(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsTailgatingDetectionSpeedThreshold(threshold, stage), 1);
    }
    
    static setEventsTailgatingDetectionReactionTimeThreshold (threshold, stage) {
        console.log("GlobalSettingsAPI setEventsTailgatingDetectionReactionTimeThreshold", threshold, stage);
        const delta = { raven: { events_config: { tailgating_detection: { alert_reaction_time: parseFloat(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsTailgatingDetectionReactionTimeThreshold(threshold, stage), 1);
    }
    
    static setEventsTailgatingDetectionFollowTimeThreshold (threshold, stage) {
        console.log("GlobalSettingsAPI setEventsTailgatingDetectionFollowTimeThreshold", threshold, stage);
        const delta = { raven: { events_config: { tailgating_detection: { follow_time: parseFloat(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsTailgatingDetectionFollowTimeThreshold(threshold, stage), 1);
    }


    static setEventsCarBumped (isEnabled, stage) {
        const delta = { raven: { events_config: { car_bumped: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsCarBumped(isEnabled, stage), 1);
    }

    static setEventsCarBumpedThreshold (threshold, stage) {
        const delta = { raven: { events_config: { car_bumped: { accel_threshold: parseInt(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsCarBumpedThreshold(threshold, stage), 1);
    }


    static setEventsHardBraking (isEnabled, stage) {
        const delta = { raven: { events_config: { hard_braking: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsHardBraking(isEnabled, stage), 1);
    }

    static setEventsHardBrakingThreshold (threshold, stage) {
        const delta = { raven: { events_config: { hard_braking: { accel_threshold: parseInt(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsHardBrakingThreshold(threshold, stage), 1);
    }

    static setEventsHardCornering (isEnabled, stage) {
        const delta = { raven: { events_config: { hard_cornering: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsHardCornering(isEnabled, stage), 1);
    }

    static setEventsHardCorneringThreshold (threshold, stage) {
        const delta = { raven: { events_config: { hard_cornering: { accel_threshold: parseInt(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsHardCorneringThreshold(threshold, stage), 1);
    }

    static setEventsIdling (isEnabled, stage) {
        const delta = { raven: { events_config: { idling: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsIdling(isEnabled, stage), 1);
    }

    static setEventsIdlingThreshold (threshold, stage) {
        const delta = { raven: { events_config: { idling: { grace_period: parseInt(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsIdlingThreshold(threshold, stage), 1);
    }

    static setEventsPossibleImpact (isEnabled, stage) {
        const delta = { raven: { events_config: { possible_impact: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsPossibleImpact(isEnabled, stage), 1);
    }

    static setEventsPossibleImpactThreshold (threshold, stage) {
        const delta = { raven: { events_config: { possible_impact: { accel_threshold: parseInt(threshold) } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setEventsPossibleImpactThreshold(threshold, stage), 1);
    }

    static setAccountDailyFleetReport (isEnabled, stage) {
        const delta = { account: { dailyFleetReport: { enabled: isEnabled } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setAccountDailyFleetReport(isEnabled, stage), 1);
    }

    static setAccountDailyFleetReports (dailyFleetReportsSettings, stage) {
        const delta = { account: { dailyFleetReport: dailyFleetReportsSettings } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setAccountDailyFleetReports(dailyFleetReportsSettings, stage), 1);
    }

    static setAccountDailyFleetReportTimezone (timezone, stage) {
        const delta = { account: { dailyFleetReport: { timezone } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setAccountDailyFleetReportTimezone(timezone, stage), 1);
    }

    static setAccountDailyFleetReportEmails (emailsArray, stage) {
        const delta = { account: { dailyFleetReport: { emails: emailsArray } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setAccountDailyFleetReportEmails(emailsArray, stage), 1);
    }

    static setGestures (isEnabled, stage) {
        console.log("GlobalSettingsAPI setGestures", isEnabled, stage);
        const delta = { raven: { system: { gesture_enabled: isEnabled } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setGestures(isEnabled, stage), 1);
    }

    static setIntegrationsCredentials (platform, accountToken, apiKey, integrationId = undefined, stage) {
        if (!stage) return Promise.reject(new Error('stage is required'));
        const integrationType = INTEGRATIONS_TYPES_NAMES[platform.toUpperCase()];
        if (!integrationType) return Promise.reject(new Error(`Invalid platform: ${platform}`));
        return GlobalSettingsAPI.getOrRefreshApiJWT(stage).then(apiJWT => {
            const apiBaseUrl = getApiBaseUrl('/user-v1');
            let url = `${apiBaseUrl}/integrations`;
            let method;
            let body;
            if (!accountToken && !apiKey && integrationId) {
                method = 'DELETE';
                url += `/${integrationId}`;
            } else {
                method = 'POST';
                body = INTEGRATIONS_DETAILS[platform.toUpperCase()]({accountToken, apiKey});
            }
            return apiRequest(url, apiJWT, method, body)
                .then(() => {
                    if (!integrationId) {
                        return GlobalSettingsAPI.loadIntegrationSettingsOnly(apiJWT, stage);
                    }
                    return Promise.resolve();
                }, e => Promise.reject(e))
                .catch(error => {
                    if (error.code === 401) {
                        return GlobalSettingsAPI.refreshTempCredentials(stage)
                            .then(() => GlobalSettingsAPI.setIntegrationsCredentials(platform, accountToken, apiKey, integrationId, stage))
                            .catch(err => { console.error("GlobalSettingsAPI setIntegrationsCredentials refreshTempCredentials error", err); return Promise.reject(err); });
                    }
                    console.log("GlobalSettingsAPI setIntegrationsCredentials error", error);
                    return Promise.reject(error);
                });
        });
    }

    static verifyIntegrationsCredentials (platform, accountToken, apiKey, stage) { // Roughed in feature to be completed when "test" endpoint is available
        if (!stage) return Promise.reject(new Error('stage is required'));
        const integrationType = INTEGRATIONS_TYPES_NAMES[platform.toUpperCase()];
        if (!integrationType) return Promise.reject(new Error(`Invalid platform: ${platform}`));
        return GlobalSettingsAPI.getOrRefreshApiJWT(stage).then(apiJWT => {
            const apiBaseUrl = getApiBaseUrl('/user-v1');
            const data = {};
            if (accountToken) data.account_token = accountToken;
            if (apiKey) data.api_key = apiKey;
            const method = 'POST';
            return apiRequest(`${apiBaseUrl}/integrations/test`, apiJWT, method, data)
                .then(() => Promise.resolve(), e => Promise.reject(e))
                .catch(error => {
                    if (error.code === 401) {
                        return GlobalSettingsAPI.refreshTempCredentials(stage)
                            .then(() => GlobalSettingsAPI.verifyIntegrationsCredentials(platform, accountToken, apiKey, stage))
                            .catch(err => { console.error("GlobalSettingsAPI verifyIntegrationsCredentials refreshTempCredentials error", err); return Promise.reject(err); });
                    }
                    console.log("GlobalSettingsAPI verifyIntegrationsCredentials error", error);
                    return Promise.reject(error);
                });
        });
    }

    static setUnitOfMeasureDistance (isMetric, stage) {
        const delta = { raven: { display: { distance_unit: isMetric ? 'METRIC' : 'IMPERIAL' } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setUnitOfMeasureDistance(isMetric, stage), 1);
    }

    static setUnitOfMeasureTemperature (isMetric, stage) {
        const delta = { raven: { display: { temperature_unit: isMetric ? 'C' : 'F' } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setUnitOfMeasureTemperature(isMetric, stage), 1);
    }

    static setWiFiHotspots (isEnabled, stage) {
        const delta = { raven: { wifi_hotspot: { hotspot_enabled: isEnabled } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setWiFiHotspots(isEnabled, stage), 1);
    }

    static setWiFiHotspotsCredentials (ssid, password, stage) {
        const delta = { raven: { wifi_hotspot: { ssid: ssid, password: password } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI.setWiFiHotspotsCredentials(ssid, password, stage), 1);
    }
}

for (let event of events){
    GlobalSettingsAPI[`setEvents${event.pascalName}`] = (isEnabled, stage) => {
        const delta = { raven: { events_config: { [event.snakeName]: { enabled: isEnabled } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI[`setEvents${event.pascalName}`](isEnabled, stage), 1);
    };
}

// Audio alert API setters for all events (hard-coded + generic)
for (let event of ALL_EVENT_NAMES){
    GlobalSettingsAPI[`setEvents${event.pascalName}AudioAlert`] = (audioAlertType, stage) => {
        const delta = { raven: { events_config: { [event.snakeName]: { audio_alert: audioAlertType } } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI[`setEvents${event.pascalName}AudioAlert`](audioAlertType, stage), 1);
    };
}

// Visual alert API setters for all events (hard-coded + generic)
// When disabling visual alert and current audio_alert is BEEP, also reset audio_alert to NONE
// (a beep without a visual cue is unhelpful to the driver; VOICE alerts remain unchanged since they describe the alert verbally)
for (let event of ALL_EVENT_NAMES){
    GlobalSettingsAPI[`setEvents${event.pascalName}VisualAlert`] = (isEnabled, stage, currentAudioAlert) => {
        const eventConfig = { visual_alert_enabled: isEnabled };
        if (isEnabled === false && currentAudioAlert === AUDIO_ALERT_TYPES.BEEP) {
            eventConfig.audio_alert = AUDIO_ALERT_TYPES.NONE;
        }
        const delta = { raven: { events_config: { [event.snakeName]: eventConfig } } };
        return GlobalSettingsAPI._patchSettings(stage, delta, () => GlobalSettingsAPI[`setEvents${event.pascalName}VisualAlert`](isEnabled, stage, currentAudioAlert), 1);
    };
}
